/**
 * PLANNER INTERACTIVO — FASE 6.6 (visualización) + FASE 6.7-A (mutación)
 *
 *     UI != autoridad (principio 1). Todo lo que este archivo ofrece
 *     mover/redimensionar/asignar viene gobernado por `evento.acciones`
 *     (la matriz de mutabilidad que ya decidió el backend,
 *     `accionesPlannerOt` en planificacion/servicio.ts) — nunca se
 *     reimplementa esa condición aquí. Toda mutación real pasa por los
 *     mismos endpoints de siempre (`PATCH .../reprogramar`,
 *     `POST .../asignaciones`), que vuelven a validar todo dentro de su
 *     propia transacción; el preview de avisos (`POST .../preview-cambio`)
 *     nunca autoriza nada, sólo informa antes de confirmar.
 *
 * Un único fetch por ventana+filtros a `GET /api/centros/:id/planificacion/planner`
 * (FASE 6.6 §B/§X), que ya compone materializadas + proyectadas + alertas +
 * capacidad — esta pantalla no recalcula nada, sólo lo presenta (y, desde
 * 6.7-A, deja mutar mediante formularios accesibles — nunca arrastre-only).
 *
 * MATERIALIZADA vs PROYECTADA: nunca sólo color (brief §C/§U). La
 * proyección lleva borde discontinuo Y el texto "Proyección" — nunca se
 * puede abrir como si fuera una OT real, nunca lleva un id de OT, nunca
 * admite reprogramar/redimensionar/asignar (brief 6.7-A §N).
 */
import { useEffect, useState, useRef, type CSSProperties, type MouseEvent as ReactMouseEvent, type ReactNode, type RefObject } from 'react';
import { Link, useLocation } from 'react-router-dom';

import {
  api,
  type AccionesPlannerOt,
  type AlertaPlanificacion,
  type CambioHipoteticoOt,
  type CargaOperarioResumen,
  type ImpactoCambio,
  type Centro,
  type Cliente,
  type MetricasDia,
  type OperacionBulkPlanificacion,
  type ResultadoAplicarCopiaSemana,
  type OperarioAsignadoEvento,
  type OtDetalle,
  type PanelPlanner,
  type PlannerEvento,
  type PlannerEventoMaterializado,
  type PlannerEventoProyectado,
  type ResultadoPlanner,
  type ResultadoPreviewCambio,
} from '../api.ts';
import { Cargando, ConfirmDialog, EmptyState, Error as ErrorCaja, EstadoLinea, EstadoOt, fecha, hoyLocal, LeyendaEstados, rangoHorario, MarcaDominio, Modal, nombresOperarios, SinResultados, useDatos, useEsMovil, Vacio } from '../componentes/base.tsx';
import { useCentroEnUrl } from '../componentes/centro-trabajo.tsx';
import { GraficoSituacionesOt, indicadoresOt, situacionesOt } from '../componentes/cifras-ot.tsx';
import { BotonExportarExcel } from '../componentes/exportar-excel.tsx';
import { ExportarRegistroLimpiezas } from '../componentes/exportar-registro-limpiezas.tsx';
import { ESTADOS_OT, estadoOtLimpieza, type DescEstado } from '../estados.ts';
import { BarraBulk, DialogoBulk } from '../componentes/planner-bulk.tsx';
import { DialogoCopiarSemana, rangoSemana } from '../componentes/planner-copiar-semana.tsx';
import { Shell } from '../componentes/shell.tsx';
import { useListaUrl } from '../listaUrl.ts';
import { useSesion } from '../contexto.tsx';
import { atencionDeOt, horarioDeOt, textoAvance } from './hoy.tsx';
import { FormularioNuevaOt } from './ots.tsx';

/**
 * FASE 6.7-A §D, CRÍTICO: construye el instante UTC de una hora LOCAL del
 * centro elegida en un formulario, usando SIEMPRE la timezone del CENTRO
 * (`centro.zonaHoraria`, IANA) — nunca la del navegador. Mismo algoritmo
 * (y misma corrección de cruce de medianoche) que los tests DST del
 * backend (`utcDeLocalMadrid`), generalizado a cualquier zona horaria.
 */
function offsetMinutos(zonaHoraria: string, fechaIso: string): number {
  const partes = new Intl.DateTimeFormat('en-US', { timeZone: zonaHoraria, timeZoneName: 'shortOffset' })
    .formatToParts(new Date(`${fechaIso}T12:00:00Z`));
  const tz = partes.find((p) => p.type === 'timeZoneName')?.value ?? 'GMT+0';
  const m = /GMT([+-]\d+)(?::(\d+))?/.exec(tz);
  if (m === null) return 0;
  const horas = Number(m[1]);
  const minutos = Number(m[2] ?? 0);
  return horas >= 0 ? horas * 60 + minutos : horas * 60 - minutos;
}
function isoDeLocal(zonaHoraria: string, fechaIso: string, horaLocal: string): string {
  const [h, mi] = horaLocal.split(':').map(Number);
  const minutosLocales = (h ?? 0) * 60 + (mi ?? 0);
  const totalMinUtc = minutosLocales - offsetMinutos(zonaHoraria, fechaIso);
  const diasDesplazados = Math.floor(totalMinUtc / 1440);
  const minutoDelDia = ((totalMinUtc % 1440) + 1440) % 1440;
  const hh = String(Math.floor(minutoDelDia / 60)).padStart(2, '0');
  const mm = String(minutoDelDia % 60).padStart(2, '0');
  const fechaUtc = new Date(Date.parse(`${fechaIso}T00:00:00Z`) + diasDesplazados * 86_400_000).toISOString().slice(0, 10);
  return `${fechaUtc}T${hh}:${mm}:00Z`;
}

export type Vista = 'dia' | 'semana' | 'mes' | 'timeline';

const DEFAULTS_PLANNER = {
  vista: 'semana',
  fecha: '',
  idCliente: '',
  idCentro: '',
  idPlan: '',
  idUbicacion: '',
  idOperario: '',
  estados: '',
  incluirAnuladas: '',
  //  FASE 6.6-B: sólo relevante en Timeline — por defecto, sólo operarios
  //  con actividad ese día (brief §4: "no llenar automáticamente la
  //  pantalla con decenas de operarios sin actividad").
  mostrarSinCarga: '',
  //  Pre-F14.2: panel operativo desplegado ('1') y su detalle NO_OK abierto ('1') — en la URL, para
  //  que al volver de una OT el Planner esté exactamente como se dejó.
  panel: '',
  noOk: '',
};

/**
 * «Hoy» es una FECHA DE CALENDARIO LOCAL, no un instante: `toISOString()` daba la de UTC y, entre las 00:00
 * y las 02:00 de Madrid, el Planner abría (y marcaba como «hoy») el día anterior. El resto de funciones de
 * fecha de este fichero son aritmética de calendario anclada a medianoche UTC y no dependen de la zona.
 */
export function hoyIso(): string {
  return hoyLocal();
}
export function sumarDias(iso: string, n: number): string {
  return new Date(Date.parse(iso) + n * 86_400_000).toISOString().slice(0, 10);
}
/** Lunes de la semana ISO que contiene `iso`. */
function lunesDeSemana(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  const diaIso = d.getUTCDay() === 0 ? 7 : d.getUTCDay();
  return sumarDias(iso, 1 - diaIso);
}
function primerDiaMes(iso: string): string {
  return `${iso.slice(0, 7)}-01`;
}
function ultimoDiaMes(iso: string): string {
  const [a, m] = iso.split('-').map(Number);
  const ultimo = new Date(Date.UTC(a ?? 2026, (m ?? 1), 0));
  return ultimo.toISOString().slice(0, 10);
}
function horas(minutos: number): string {
  const h = minutos / 60;
  return `${Number.isInteger(h) ? String(h) : h.toFixed(1)}h`;
}
/** FASE 6.6 §M: capacidad desconocida nunca se muestra como "0h". */
function horasOSinConfigurar(minutos: number | null, sinConfigurar: number): string {
  if (minutos === null) return 'Sin configurar';
  return sinConfigurar > 0 ? `${horas(minutos)} conocidas (+${String(sinConfigurar)} sin configurar)` : horas(minutos);
}
const FORMATO_DIA_CORTO = new Intl.DateTimeFormat('es-ES', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
function etiquetaDiaCorta(iso: string): string {
  return FORMATO_DIA_CORTO.format(new Date(`${iso}T00:00:00Z`));
}
const FORMATO_DIA_LARGO = new Intl.DateTimeFormat('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
export function etiquetaDiaLarga(iso: string): string {
  return FORMATO_DIA_LARGO.format(new Date(`${iso}T00:00:00Z`));
}

/**
 * Ventana [desde,hasta] que corresponde a cada vista — nunca se pide más de
 * lo visible. Timeline = un solo día (brief 6.6-B §3: "la prioridad es
 * timeline diario por operario usable y correcto" — no se implementa
 * semanal: una matriz operario×7días×horas dejaría de ser legible en el
 * ancho de una pantalla real, y el brief pide justificar antes de intentarlo).
 */
export function rangoDeVista(vista: Vista, fecha: string): { desde: string; hasta: string } {
  if (vista === 'dia' || vista === 'timeline') return { desde: fecha, hasta: fecha };
  if (vista === 'semana') {
    const lunes = lunesDeSemana(fecha);
    return { desde: lunes, hasta: sumarDias(lunes, 6) };
  }
  return { desde: primerDiaMes(fecha), hasta: ultimoDiaMes(fecha) };
}

function inicioLocalDeEvento(ev: PlannerEvento): string {
  if (ev.tipo === 'OT') return ev.inicioLocal ?? `${ev.fecha}T00:00:00`;
  return `${ev.fecha}T${ev.hora}`;
}
function duracionDeEvento(ev: PlannerEvento): number {
  return ev.tipo === 'OT' ? ev.duracionPlanificadaMin : ev.duracionEstimadaMin;
}
/** FASE 6.6 §D: el código/referencia es mínimo obligatorio — nunca sólo la sala o el plan. */
function resumenDeEvento(ev: PlannerEvento): string {
  if (ev.tipo === 'OT') return `${ev.codigo} · ${ev.codigoSala}`;
  return `${ev.codigoSala} · ${ev.lineas.map((l) => l.referenciaTarea).join(', ')}`;
}

/**
 * FASE 6.7-B1: modo selección múltiple. Sólo OTs MATERIALIZADAS (`idOt`) —
 * una proyección nunca lleva checkbox. Seleccionar no modifica nada: sólo
 * decide sobre qué OTs se pedirá el preview bulk. Qué se puede hacer con
 * cada una lo decide el backend en ese preview (NO_ELEGIBLE con motivo).
 */
interface SeleccionPlanner {
  readonly activa: boolean;
  readonly ids: ReadonlySet<string>;
  readonly alternar: (idOt: string) => void;
}

export function PaginaPlanner(): ReactNode {
  const lista = useListaUrl(DEFAULTS_PLANNER);
  const vista = (lista.valor('vista') || 'semana') as Vista;
  const fecha = lista.valor('fecha') || hoyIso();
  const idCliente = lista.valor('idCliente');
  const idCentro = lista.valor('idCentro');
  const idPlan = lista.valor('idPlan');
  const idUbicacion = lista.valor('idUbicacion');
  const idOperario = lista.valor('idOperario');
  const estados = lista.valor('estados');
  const incluirAnuladas = lista.valor('incluirAnuladas') === '1';
  const mostrarSinCarga = lista.valor('mostrarSinCarga') === '1';
  const panelAbierto = lista.valor('panel') === '1';
  const detalleNoOk = lista.valor('noOk') === '1';
  const ubicacionActual = useLocation();
  const esMovil = useEsMovil();
  const { puede } = useSesion();
  const volverA = (useLocation().state as { volver?: { a: string; texto: string } } | null)?.volver ?? null;
  //  F10.1: sin centro en la URL, el del usuario (único o el último elegido) — y se recuerda cada elección.
  useCentroEnUrl(idCliente, idCentro, (c) => lista.fijarVarios(c));

  const [eventoAbierto, setEventoAbierto] = useState<PlannerEvento | null>(null);
  /** FASE 6.7-A §M: clic en un hueco → mismo formulario de OT manual, con fecha/hora ya elegidas. */
  const [creandoDesdeHueco, setCreandoDesdeHueco] = useState<{ fecha: string; hora: string } | null>(null);
  const [modoSeleccion, setModoSeleccion] = useState(false);
  const [idsSeleccionados, setIdsSeleccionados] = useState<ReadonlySet<string>>(new Set());
  const [dialogoBulk, setDialogoBulk] = useState<OperacionBulkPlanificacion | null>(null);
  const [mensajeBulk, setMensajeBulk] = useState<string | null>(null);
  //  FASE 6.7-B2: Copiar semana — por PERMISOS (manual u ocurrencias del plan), nunca por rol.
  const [copiandoSemana, setCopiandoSemana] = useState(false);
  const [copiaHecha, setCopiaHecha] = useState<ResultadoAplicarCopiaSemana | null>(null);
  const puedeCopiarSemana = puede('ots:crear_manual') || puede('ots:generar');
  //  FASE 6.7 (UAT): el panel de alertas del Planner (contexto general) se
  //  puede abrir desde el impacto de un cambio — «Ver avisos del Planner».
  const [alertasAbiertas, setAlertasAbiertas] = useState(false);
  const panelAlertasRef = useRef<HTMLDivElement>(null);
  function verAvisosPlanner(): void {
    setEventoAbierto(null);
    setAlertasAbiertas(true);
    setTimeout(() => panelAlertasRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }), 0);
  }
  const puedeBulkReprogramar = puede('ots:reprogramar');
  const puedeBulkAsignar = puede('ots:asignar');

  const clientes = useDatos(() => api.get<{ clientes: Cliente[] }>('/api/clientes'), []);
  //  Pre-F14.2 (bug del VPS): Cliente = Todos → todos los centros accesibles; Cliente X → sólo los suyos.
  //  La URL es la única fuente de verdad de cliente, centro, plan y periodo: Planner y panel leen de ella.
  const centrosDelCliente = useDatos(
    () => api.get<{ centros: Centro[] }>(idCliente === '' ? '/api/centros' : `/api/clientes/${idCliente}/centros`),
    [idCliente],
  );
  const centrosVisibles = centrosDelCliente.datos?.centros ?? [];
  const centroActual = centrosVisibles.find((c) => c.id === idCentro) ?? null;
  const planesDelCentro = useDatos(
    () => (idCentro === '' ? Promise.resolve({ planes: [] as { id: string; codigo: string }[] }) : api.get<{ planes: { id: string; codigo: string }[] }>(`/api/centros/${idCentro}/planes`)),
    [idCentro],
  );

  const { desde, hasta } = rangoDeVista(vista, fecha);
  const consulta = new URLSearchParams({ desde, hasta });
  //  El backend comprueba que el centro es de ese cliente: un filtro obsoleto nunca pinta datos.
  if (idCliente !== '') consulta.set('idCliente', idCliente);
  if (idPlan !== '') consulta.set('idPlan', idPlan);
  if (idUbicacion !== '') consulta.set('idUbicacion', idUbicacion);
  if (idOperario !== '') consulta.set('idOperario', idOperario);
  if (estados !== '') consulta.set('estados', estados);
  if (incluirAnuladas) consulta.set('incluirAnuladas', 'true');

  const planner = useDatos(
    () => (idCentro === '' ? Promise.resolve(null) : api.get<ResultadoPlanner>(`/api/centros/${idCentro}/planificacion/planner?${consulta.toString()}`)),
    [idCentro, idCliente, desde, hasta, idPlan, idUbicacion, idOperario, estados, incluirAnuladas],
  );

  //  FASE 6.7-B1 §X: nunca conservar ids que ya no están en lo visible
  //  (cambio de rango, filtro o refetch tras éxito) — la selección es
  //  siempre un subconjunto de las OTs pintadas ahora mismo.
  const eventosActuales = planner.datos?.eventos;
  useEffect(() => {
    const visibles = new Set((eventosActuales ?? []).flatMap((ev) => (ev.tipo === 'OT' ? [ev.idOt] : [])));
    setIdsSeleccionados((actual) => {
      const podada = [...actual].filter((id) => visibles.has(id));
      return podada.length === actual.size ? actual : new Set(podada);
    });
  }, [eventosActuales]);

  function alternarSeleccion(idOt: string): void {
    setMensajeBulk(null);
    setIdsSeleccionados((actual) => {
      const nueva = new Set(actual);
      if (nueva.has(idOt)) nueva.delete(idOt); else nueva.add(idOt);
      return nueva;
    });
  }
  function salirDeSeleccion(): void {
    setModoSeleccion(false);
    setIdsSeleccionados(new Set());
    setDialogoBulk(null);
  }
  const seleccion: SeleccionPlanner = { activa: modoSeleccion, ids: idsSeleccionados, alternar: alternarSeleccion };
  const eventosSeleccionados = (planner.datos?.eventos ?? []).filter(
    (ev): ev is PlannerEventoMaterializado => ev.tipo === 'OT' && idsSeleccionados.has(ev.idOt),
  );
  const operariosDeSeleccion: OperarioAsignadoEvento[] = [
    ...new Map(eventosSeleccionados.flatMap((ev) => ev.operarios).map((o) => [o.idOperario, o])).values(),
  ];

  function irA(nuevaFecha: string, nuevaVista?: Vista): void {
    lista.fijarVarios({ fecha: nuevaFecha, ...(nuevaVista !== undefined ? { vista: nuevaVista } : {}) });
  }
  function navegar(delta: number): void {
    if (vista === 'dia' || vista === 'timeline') irA(sumarDias(fecha, delta));
    else if (vista === 'semana') irA(sumarDias(fecha, delta * 7));
    else {
      const d = new Date(`${fecha}T00:00:00Z`);
      d.setUTCMonth(d.getUTCMonth() + delta);
      irA(d.toISOString().slice(0, 10));
    }
  }

  const contadorFiltros = [idPlan !== '', idUbicacion !== '', idOperario !== '', estados !== '', incluirAnuladas].filter(Boolean).length;

  return (
    <Shell
      //  F10.4: llegando desde un plan («Ver en el Planner»), la miga vuelve a él.
      migas={[...(volverA === null ? [] : [volverA]), { texto: 'Planner de limpieza' }]}
      titulo="Planner de limpieza"
      subtitulo="Cuándo se hace cada OT: consulta, reprograma y asigna el trabajo del centro."
    >
      <div className="panel" style={{ marginBottom: 16 }}>
        <div className="panel-cuerpo">
          <div className="fila" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div className="campo">
              <label htmlFor="planner-cliente">Cliente</label>
              <select
                id="planner-cliente"
                value={idCliente}
                //  Cambiar de cliente limpia el centro (y lo que depende de él: plan y ubicación).
                onChange={(e) => lista.fijarVarios({ idCliente: e.target.value, idCentro: '', idPlan: '', idUbicacion: '' })}
              >
                <option value="">Todos los clientes</option>
                {(clientes.datos?.clientes ?? []).map((c) => (
                  <option key={c.id} value={c.id}>{c.razonSocial}</option>
                ))}
              </select>
            </div>
            <div className="campo">
              <label htmlFor="planner-centro">Centro</label>
              <select
                id="planner-centro"
                value={idCentro}
                onChange={(e) => {
                  //  Con «Todos los clientes», elegir un centro fija también su cliente (URL coherente).
                  const elegido = centrosVisibles.find((c) => c.id === e.target.value);
                  lista.fijarVarios({ idCentro: e.target.value, idPlan: '', idUbicacion: '', ...(elegido !== undefined ? { idCliente: elegido.idCliente } : {}) });
                }}
              >
                <option value="">Elige un centro…</option>
                {centrosVisibles.map((c) => (
                  <option key={c.id} value={c.id}>{idCliente === '' ? `${c.descripcion} · ${c.clienteRazonSocial}` : c.descripcion}</option>
                ))}
              </select>
            </div>
            <nav className="fila" style={{ gap: 4 }} aria-label="Vista del planner">
              {(['dia', 'semana', 'mes', 'timeline'] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  className={vista === v ? 'principal' : 'secundario'}
                  aria-pressed={vista === v}
                  onClick={() => lista.fijarVarios({ vista: v })}
                >
                  {v === 'dia' ? 'Día' : v === 'semana' ? 'Semana' : v === 'mes' ? 'Mes' : 'Timeline'}
                </button>
              ))}
            </nav>
            <nav className="fila" style={{ gap: 4 }} aria-label="Navegar por fecha">
              <button type="button" onClick={() => navegar(-1)} aria-label="Período anterior">←</button>
              <button type="button" onClick={() => irA(hoyIso())}>Hoy</button>
              <button type="button" onClick={() => navegar(1)} aria-label="Período siguiente">→</button>
            </nav>
            <span className="pequena suave">{vista === 'mes' ? fecha.slice(0, 7) : `${etiquetaDiaCorta(desde)} – ${etiquetaDiaCorta(hasta)}`}</span>
            {vista === 'timeline' && (
              <label className="fila" style={{ gap: 6 }}>
                <input
                  type="checkbox"
                  checked={mostrarSinCarga}
                  onChange={(e) => lista.fijar('mostrarSinCarga', e.target.checked ? '1' : '')}
                />
                Mostrar sin carga
              </label>
            )}
          </div>

          <details style={{ marginTop: 10 }} open={contadorFiltros > 0}>
            <summary className="pequena">Filtros{contadorFiltros > 0 ? ` (${String(contadorFiltros)})` : ''}</summary>
            <div className="fila" style={{ gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
              <div className="campo">
                <label htmlFor="planner-plan">Plan</label>
                <select id="planner-plan" value={idPlan} onChange={(e) => lista.fijar('idPlan', e.target.value)}>
                  <option value="">Todos</option>
                  {(planesDelCentro.datos?.planes ?? []).map((p) => (
                    <option key={p.id} value={p.id}>{p.codigo}</option>
                  ))}
                </select>
              </div>
              <div className="campo">
                <label htmlFor="planner-estado">Estado</label>
                <select id="planner-estado" value={estados} onChange={(e) => lista.fijar('estados', e.target.value)}>
                  <option value="">Todos (salvo anuladas)</option>
                  {['PLANIFICADA', 'EN_CURSO', 'COMPLETADA', 'VERIFICADA', 'CERRADA', 'REABIERTA', 'NO_REALIZADA'].map((s) => (
                    <option key={s} value={s}>{estadoOtLimpieza(s).label}</option>
                  ))}
                </select>
              </div>
              <label className="fila" style={{ gap: 6 }}>
                <input type="checkbox" checked={incluirAnuladas} onChange={(e) => lista.fijar('incluirAnuladas', e.target.checked ? '1' : '')} />
                Incluir anuladas
              </label>
              {contadorFiltros > 0 && (
                <button type="button" className="enlace" onClick={() => lista.fijarVarios({ idPlan: '', idUbicacion: '', idOperario: '', estados: '', incluirAnuladas: '' })}>
                  Limpiar filtros
                </button>
              )}
            </div>
          </details>
        </div>
      </div>

      {/* PC-v2.2-C2.3-B: registro E3 del cliente del centro elegido (sus centros alcanzables, si se pide «todos»). */}
      {idCentro !== '' && centroActual !== null && (
        <ExportarRegistroLimpiezas
          perspectiva="INTERNA" idCliente={centroActual.idCliente} idCentro={idCentro}
          varios={centrosVisibles.filter((c) => c.idCliente === centroActual.idCliente).length > 1}
          desdeVista={desde} hastaVista={hasta}
        />
      )}

      {idCentro !== '' && planner.datos?.panel !== undefined && (
        <PanelOperativo
          panel={planner.datos.panel}
          vista={vista}
          desde={desde}
          hasta={hasta}
          abierto={panelAbierto}
          alternar={() => lista.fijarVarios({ panel: panelAbierto ? '' : '1', noOk: '' })}
          detalleNoOk={detalleNoOk}
          alternarNoOk={() => lista.fijarVarios({ panel: '1', noOk: detalleNoOk ? '' : '1' })}
          fijarVista={(v) => lista.fijarVarios({ vista: v })}
          volver={{ a: `${ubicacionActual.pathname}${ubicacionActual.search}`, texto: 'Volver al Planner' }}
          rutaExportar={puede('calidad:indicadores') ? `/api/centros/${idCentro}/planificacion/panel/exportar?${consulta.toString()}&vista=${vista}` : null}
        />
      )}

      {idCentro !== '' && centroActual !== null && (puede('ots:crear_manual') || puedeBulkReprogramar || puedeBulkAsignar || puedeCopiarSemana) && (
        <div className="fila" style={{ marginBottom: 16, gap: 8, flexWrap: 'wrap' }}>
          {puede('ots:crear_manual') && !modoSeleccion && (
            <button type="button" className="secundario" onClick={() => setCreandoDesdeHueco({ fecha, hora: '09:00' })}>
              + Nueva OT en {etiquetaDiaCorta(fecha)}
            </button>
          )}
          {(puedeBulkReprogramar || puedeBulkAsignar) && (
            <button
              type="button" className={modoSeleccion ? 'principal' : 'secundario'} aria-pressed={modoSeleccion}
              onClick={() => { setMensajeBulk(null); if (modoSeleccion) salirDeSeleccion(); else setModoSeleccion(true); }}
            >
              {/*  F10.4: el botón dice para qué sirve — reprogramar varias OT no estaba a la vista. */}
              {modoSeleccion ? 'Salir de selección' : puedeBulkReprogramar ? 'Reprogramar o asignar varias' : 'Asignar varias'}
            </button>
          )}
          {vista === 'semana' && puedeCopiarSemana && !modoSeleccion && (
            <button type="button" className="secundario" onClick={() => { setCopiaHecha(null); setCopiandoSemana(true); }}>
              Copiar semana
            </button>
          )}
        </div>
      )}
      {mensajeBulk !== null && <p className="nota exito" role="status">{mensajeBulk}</p>}
      {copiaHecha !== null && (
        <div className="nota exito fila" role="status" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <span>
            Semana copiada a {rangoSemana(copiaHecha.semanaDestino)}: {copiaHecha.manualesCreadas} OT manual(es) creada(s),{' '}
            {copiaHecha.ocurrenciasMaterializadas} ocurrencia(s) del plan materializada(s), {copiaHecha.asignacionesCopiadas} asignación(es) copiada(s).
          </span>
          <button type="button" className="secundario" onClick={() => { irA(copiaHecha.semanaDestino, 'semana'); setCopiaHecha(null); }}>
            Ver semana copiada
          </button>
        </div>
      )}
      {modoSeleccion && centroActual !== null && (
        <>
          <BarraBulk
            seleccionadas={idsSeleccionados.size}
            puedeReprogramar={puedeBulkReprogramar}
            puedeAsignar={puedeBulkAsignar}
            abrir={(op) => { setMensajeBulk(null); setDialogoBulk(op); }}
            limpiar={() => setIdsSeleccionados(new Set())}
          />
          {vista === 'mes' && (
            <p className="nota info" role="status">La selección múltiple está disponible en Día, Semana y Timeline. El Mes es sólo un resumen.</p>
          )}
        </>
      )}

      {idCentro === '' && <Vacio titulo="Elige un centro para ver su planificación." />}
      {idCentro !== '' && planner.cargando && planner.datos === null && <Cargando texto="Cargando planificación…" />}
      {idCentro !== '' && planner.error !== null && <ErrorCaja error={planner.error} reintentar={planner.recargar} />}
      {planner.datos !== null && planner.datos.eventos.length > 0 && vista !== 'mes' && (
        <LeyendaEstados titulo="Estados de OT" estados={LEYENDA_LIMPIEZA}
          extra={<li className="leyenda-extra"><span aria-hidden="true">⚠</span> Conflicto o aviso (aparte del estado)</li>} />
      )}
      {planner.datos !== null && (
        <>
          {planner.datos.eventos.length === 0 && (
            //  UX-1C: vacío REAL vs vacío por filtros; texto propio de Limpieza (no el genérico).
            idPlan !== '' || idUbicacion !== '' || idOperario !== '' || estados !== ''
              ? <SinResultados />
              : <EmptyState titulo="No hay órdenes planificadas para este periodo." descripcion="Cambia de periodo o de centro, o crea una OT manual." />
          )}
          {vista === 'dia' && (
            esMovil
              ? <ListaCronologica eventos={planner.datos.eventos} abrir={setEventoAbierto} seleccion={seleccion} />
              : (
                <VistaDiaHoraria
                  dias={[desde]} eventos={planner.datos.eventos} abrir={setEventoAbierto}
                  idCentro={idCentro} zonaHoraria={centroActual?.zonaHoraria ?? 'UTC'} desde={desde} hasta={hasta}
                  alGuardado={() => planner.recargar()} seleccion={seleccion}
                />
              )
          )}
          {vista === 'semana' && (
            esMovil
              ? <ListaCronologica eventos={planner.datos.eventos} abrir={setEventoAbierto} agruparPorDia seleccion={seleccion} />
              : (
                <VistaDiaHoraria
                  dias={diasDelRango(desde, hasta)} eventos={planner.datos.eventos} abrir={setEventoAbierto}
                  idCentro={idCentro} zonaHoraria={centroActual?.zonaHoraria ?? 'UTC'} desde={desde} hasta={hasta}
                  alGuardado={() => planner.recargar()} seleccion={seleccion}
                  verDia={(f) => irA(f, 'dia')}
                />
              )
          )}
          {vista === 'mes' && (
            <VistaMes fecha={fecha} dias={planner.datos.dias} eventos={planner.datos.eventos} irADia={(f) => irA(f, 'dia')} />
          )}
          {/* UX-1D-B: la misma línea temporal en todos los anchos (en móvil se desplaza dentro de su contenedor). */}
          {vista === 'timeline' && (
            <VistaTimeline fecha={fecha} eventos={planner.datos.eventos} operariosResumen={planner.datos.operarios} mostrarSinCarga={mostrarSinCarga} abrir={setEventoAbierto} seleccion={seleccion} />
          )}

          <BandejaPendiente eventos={planner.datos.eventos} abrir={setEventoAbierto} />
          {planner.datos.alertas.length > 0 && (
            <div ref={panelAlertasRef}>
              <PanelAlertas alertas={planner.datos.alertas} abierto={alertasAbiertas} alternar={() => setAlertasAbiertas((v) => !v)} />
            </div>
          )}
        </>
      )}

      {eventoAbierto?.tipo === 'OT' && centroActual !== null && (
        <PanelDetalleOt
          evento={eventoAbierto}
          zonaHoraria={centroActual.zonaHoraria}
          cerrar={() => setEventoAbierto(null)}
          alGuardado={() => planner.recargar()}
          verAvisosPlanner={verAvisosPlanner}
        />
      )}
      {eventoAbierto?.tipo === 'PROYECCION' && (
        <PanelDetalleProyeccion evento={eventoAbierto} cerrar={() => setEventoAbierto(null)} />
      )}
      {copiandoSemana && centroActual !== null && (
        <DialogoCopiarSemana
          idCentro={idCentro}
          semanaOrigen={desde}
          puedeAsignar={puede('ots:asignar')}
          cerrar={() => setCopiandoSemana(false)}
          alTerminar={(r) => {
            //  Se queda en la semana actual (no desorienta) con feedback y
            //  la opción explícita de ir a la copiada; un refetch único.
            setCopiandoSemana(false);
            setCopiaHecha(r);
            planner.recargar();
          }}
        />
      )}
      {dialogoBulk !== null && centroActual !== null && (
        <DialogoBulk
          operacion={dialogoBulk}
          idCentro={idCentro}
          idsOt={[...idsSeleccionados]}
          operariosDeSeleccion={operariosDeSeleccion}
          quitarDeSeleccion={alternarSeleccion}
          cerrar={() => setDialogoBulk(null)}
          alTerminar={(mensaje) => {
            //  §X: un único refetch del Planner (eventos, alertas, carga,
            //  bandeja, timeline — todo sale de esa respuesta) y selección limpia.
            setDialogoBulk(null);
            setIdsSeleccionados(new Set());
            setMensajeBulk(mensaje);
            planner.recargar();
          }}
        />
      )}
      {creandoDesdeHueco !== null && centroActual !== null && (
        <Modal titulo="Nueva OT" cerrar={() => setCreandoDesdeHueco(null)}>
          <FormularioNuevaOt
            centroFijo={centroActual}
            fechaInicial={creandoDesdeHueco.fecha}
            horaInicial={creandoDesdeHueco.hora}
            alCancelar={() => setCreandoDesdeHueco(null)}
            alCrear={() => {
              setCreandoDesdeHueco(null);
              planner.recargar();
            }}
          />
        </Modal>
      )}
    </Shell>
  );
}

export function diasDelRango(desde: string, hasta: string): string[] {
  const dias: string[] = [];
  for (let f = desde; f <= hasta; f = sumarDias(f, 1)) dias.push(f);
  return dias;
}

// ------------------------------------------------------------- vista día/semana (horaria)

const HORA_INICIO_DEFECTO = 6;
const HORA_FIN_DEFECTO = 22;
const ALTURA_HORA_PX = 48;

/**
 * FASE 6.7-A §E: granularidad única de drag/resize — una sola constante,
 * nunca repetida/hardcodeada en cada componente. No cambia cómo se
 * REPRESENTA un evento existente (un inicio a las 08:07 se sigue viendo a
 * las 08:07): el snap sólo se aplica AL INTERACTUAR, redondeando lo que el
 * usuario arrastra, nunca lo que ya había.
 */
const PLANNER_INTERVALO_MINUTOS = 15;
function redondearAIntervalo(minutos: number): number {
  return Math.round(minutos / PLANNER_INTERVALO_MINUTOS) * PLANNER_INTERVALO_MINUTOS;
}
/** Píxeles de arrastre vertical → minutos, ya redondeados al intervalo — pura, sin DOM, para poder probarla sin layout real. */
function minutosDesdeDeltaPx(deltaPx: number, alturaHoraPx: number = ALTURA_HORA_PX): number {
  return redondearAIntervalo((deltaPx / alturaHoraPx) * 60);
}

function minutoInicioDeEvento(ev: PlannerEvento): number {
  const [, hh, mm] = /T(\d{2}):(\d{2})/.exec(inicioLocalDeEvento(ev)) ?? [];
  return Number(hh ?? 0) * 60 + Number(mm ?? 0);
}

/**
 * FASE 6.6-B §12: eje DINÁMICO, nunca más estrecho que 06:00–22:00, pero se
 * amplía (hora entera hacia abajo/arriba) si hay un evento antes de las 6 o
 * que termina después de las 22 — nunca se recorta en silencio un turno
 * temprano/nocturno. Un evento que cruza medianoche se recorta VISUALMENTE
 * a las 24:00 de ESTE día (es un límite razonable, no infinito): su
 * duración real sigue íntegra en el `aria-label` y en el panel de detalle,
 * nunca se pierde información, sólo la barra no puede dibujarse más allá
 * del día que se está mostrando.
 */
function rangoHorasDia(eventos: readonly PlannerEvento[]): { inicio: number; fin: number } {
  let inicio = HORA_INICIO_DEFECTO;
  let fin = HORA_FIN_DEFECTO;
  for (const ev of eventos) {
    const ini = minutoInicioDeEvento(ev);
    const finEv = Math.min(ini + duracionDeEvento(ev), 24 * 60);
    inicio = Math.min(inicio, Math.floor(ini / 60));
    fin = Math.max(fin, Math.ceil(finEv / 60));
  }
  return { inicio: Math.max(0, inicio), fin: Math.min(24, Math.max(fin, inicio + 1)) };
}

interface EventoPosicionado {
  readonly evento: PlannerEvento;
  readonly minutoInicio: number;
  readonly minutoFin: number;
  readonly finReales: number;
  readonly columna: number;
  readonly columnas: number;
  /** Grupo de solapamiento (eventos que se pisan entre sí en el mismo día). */
  readonly grupo: number;
}

/** UX-1D — en Semana, las OT que no caben en un tramo solapado se resumen en un bloque «+N». */
interface ResumenSolapadas {
  readonly grupo: number;
  readonly minutoInicio: number;
  readonly minutoFin: number;
  readonly columna: number;
  readonly columnas: number;
  readonly eventos: readonly PlannerEvento[];
}

/**
 * UX-1D — Semana legible con mucha carga (patrón «+N más» de cualquier
 * calendario): en cada tramo solapado se ven como mucho `max` carriles; si
 * hay más OT, se pintan `max - 1` y un bloque que resume el resto (con la
 * forma de estado de cada una) y abre ese día en la vista Día, donde se ven
 * todas. No cambia qué eventos hay ni cómo se mueven: sólo cómo se agrupan.
 */
function limitarCarriles(posicionados: readonly EventoPosicionado[], max: number): { visibles: EventoPosicionado[]; resumenes: ResumenSolapadas[] } {
  const visibles: EventoPosicionado[] = [];
  const ocultos = new Map<number, EventoPosicionado[]>();
  for (const x of posicionados) {
    if (x.columnas <= max) { visibles.push(x); continue; }
    if (x.columna < max - 1) visibles.push({ ...x, columnas: max });
    else ocultos.set(x.grupo, [...(ocultos.get(x.grupo) ?? []), x]);
  }
  const resumenes = [...ocultos.entries()].map(([grupo, xs]): ResumenSolapadas => ({
    grupo, columna: max - 1, columnas: max,
    minutoInicio: Math.min(...xs.map((x) => x.minutoInicio)),
    minutoFin: Math.max(...xs.map((x) => x.minutoFin)),
    eventos: xs.map((x) => x.evento),
  }));
  return { visibles, resumenes };
}

/** Máximo de carriles por tramo en Semana; en Día se ven siempre todos. */
const MAX_CARRILES_SEMANA = 3;
/** Ancho mínimo de cada día en Semana (hora + forma del estado legibles en 3 carriles). */
const ANCHO_MIN_DIA_SEMANA_PX = 144;

/**
 * Empaquetado por columnas: solapamientos lado a lado, nunca ocultos (brief §F/§O).
 * `minEmpaquetado` (UX-1D-B): duración mínima SÓLO para repartir carriles — el Timeline la sube
 * para que el ancho mínimo visual de una OT corta nunca pise a la siguiente del mismo carril. El
 * ancho dibujado (`minutoFin`) sigue saliendo de la duración real.
 */
function posicionarEventos(eventos: readonly PlannerEvento[], minEmpaquetado = 15): EventoPosicionado[] {
  const base = eventos
    .map((ev) => {
      const minutoInicio = minutoInicioDeEvento(ev);
      const finReales = minutoInicio + Math.max(duracionDeEvento(ev), 15);
      //  Recorte SÓLO visual/de empaquetado a medianoche — ver cabecera de `rangoHorasDia`.
      const finEmpaquetado = Math.min(minutoInicio + Math.max(duracionDeEvento(ev), minEmpaquetado), 24 * 60);
      return { evento: ev, minutoInicio, minutoFin: Math.min(finReales, 24 * 60), finReales, finEmpaquetado };
    })
    .sort((a, b) => a.minutoInicio - b.minutoInicio);

  const columnas: { finMin: number }[] = [];
  const resultado: (EventoPosicionado & { grupo: number })[] = [];
  let grupoActual = 0;
  let finGrupo = -1;
  const asignaciones: { item: (typeof base)[number]; columna: number; grupo: number }[] = [];

  for (const item of base) {
    if (item.minutoInicio >= finGrupo) {
      grupoActual += 1;
      finGrupo = item.finEmpaquetado;
      columnas.length = 0;
    } else {
      finGrupo = Math.max(finGrupo, item.finEmpaquetado);
    }
    let col = columnas.findIndex((c) => c.finMin <= item.minutoInicio);
    if (col === -1) {
      col = columnas.length;
      columnas.push({ finMin: item.finEmpaquetado });
    } else {
      columnas[col] = { finMin: item.finEmpaquetado };
    }
    asignaciones.push({ item, columna: col, grupo: grupoActual });
  }
  for (const a of asignaciones) {
    const totalColumnasDelGrupo = asignaciones.filter((x) => x.grupo === a.grupo).reduce((m, x) => Math.max(m, x.columna + 1), 1);
    const { finEmpaquetado: _, ...item } = a.item;
    resultado.push({ ...item, columna: a.columna, columnas: totalColumnasDelGrupo, grupo: a.grupo });
  }
  return resultado;
}

export function VistaDiaHoraria({
  dias, eventos, abrir, idCentro, zonaHoraria, desde, hasta, alGuardado, seleccion, verDia, soloLectura = false,
}: {
  dias: readonly string[]; eventos: readonly PlannerEvento[]; abrir: (ev: PlannerEvento) => void;
  idCentro: string; zonaHoraria: string; desde: string; hasta: string; alGuardado: () => void;
  seleccion?: SeleccionPlanner;
  /** UX-1D: abre un día en la vista Día (bloque «+N» de la Semana). */
  verDia?: (fecha: string) => void;
  /** Portal cliente v2: nada se arrastra ni se estira, diga lo que diga `evento.acciones`. */
  soloLectura?: boolean;
}): ReactNode {
  const eventosVisibles = eventos.filter((ev) => dias.includes(ev.fecha));
  //  MISMO eje para todos los días de la vista (brief §12) — si cualquiera
  //  de los días tiene un evento temprano/tardío, todas las columnas lo
  //  reflejan, para que sigan siendo comparables entre sí.
  const { inicio: horaInicio, fin: horaFin } = rangoHorasDia(eventosVisibles);
  const horas = Array.from({ length: horaFin - horaInicio }, (_, i) => horaInicio + i);
  //  FASE 6.7-A (drag entre días, brief "final gate"): el ancho REAL de
  //  columna se mide en el momento de arrastrar (mousedown), no se asume —
  //  `gridRef` es la única fuente, tanto en el navegador real como en los
  //  tests (que la mockean con `getBoundingClientRect`).
  const gridRef = useRef<HTMLDivElement>(null);
  //  UX-1D: en semanas densas (muchas OT a la vez) las columnas no se aprietan hasta ser tiras
  //  ilegibles. En Semana, cada tramo solapado enseña como mucho MAX_CARRILES_SEMANA carriles y
  //  resume el resto («+N»); en Día, todos, y la rejilla gana ancho según la carga. Las columnas
  //  siguen siendo IGUALES (el arrastre entre días mide el ancho real de la rejilla).
  const semana = dias.length > 1;
  //  En modo selección (acciones en lote) cada OT necesita su checkbox: nunca se resume en «+N».
  const seleccionando = seleccion?.activa === true;
  const agrupar = semana && !seleccionando;
  const posicionadosPorDia = new Map(dias.map((d) => [d, posicionarEventos(eventos.filter((ev) => ev.fecha === d))]));
  const porDia = new Map(dias.map((d) => [d, agrupar ? limitarCarriles(posicionadosPorDia.get(d) ?? [], MAX_CARRILES_SEMANA) : { visibles: posicionadosPorDia.get(d) ?? [], resumenes: [] }]));
  const maxCarriles = Math.max(1, ...[...posicionadosPorDia.values()].flatMap((ps) => ps.map((x) => x.columnas)));
  const anchoMinimo = 56 + dias.length * (agrupar ? ANCHO_MIN_DIA_SEMANA_PX
    : Math.max(semana ? ANCHO_MIN_DIA_SEMANA_PX : 0, maxCarriles * (seleccionando ? ANCHO_MIN_CARRIL_SELECCION_PX : ANCHO_MIN_CARRIL_PX)));
  const hoy = hoyIso();
  //  UX-1D-B: días desplegados en Semana («+N más» → todas las OT del día, sin salir de Semana).
  const [desplegados, setDesplegados] = useState<ReadonlySet<string>>(new Set());
  const alternarDia = (d: string): void => setDesplegados((s) => { const n = new Set(s); if (n.has(d)) n.delete(d); else n.add(d); return n; });
  const hayDesplegados = agrupar && dias.some((d) => desplegados.has(d));
  return (
    <div className="planner-grid-scroll">
      <div ref={gridRef} className="planner-grid" style={{ gridTemplateColumns: `56px repeat(${String(dias.length)}, minmax(0, 1fr))`, minWidth: Math.max(640, anchoMinimo) }}>
        <div className="planner-grid-esquina" />
        {dias.map((d) => {
          const numOts = (posicionadosPorDia.get(d) ?? []).filter((x) => x.evento.tipo === 'OT').length;
          return (
            <div key={d} className={`planner-grid-cabecera-dia${d === hoy ? ' hoy' : ''}`} aria-current={d === hoy ? 'date' : undefined}>
              <span className="planner-dia-fecha">{etiquetaDiaCorta(d)}</span>
              {numOts > 0 && <span className="planner-dia-carga">{numOts} OT</span>}
            </div>
          );
        })}
        {hayDesplegados && dias.filter((d) => desplegados.has(d)).map((d) => (
          //  Fila propia a todo el ancho de la semana: crece en vertical; las columnas de los días no cambian.
          <div key={`desp-${d}`} className="planner-grid-desplegado" style={{ gridColumn: '1 / -1' }}>
            <DiaDesplegado id={idDesplegado(d)} fecha={d} eventos={eventos.filter((ev) => ev.fecha === d)} abrir={abrir}
              plegar={() => alternarDia(d)} verDia={verDia === undefined ? undefined : () => verDia(d)} />
          </div>
        ))}
        <div className="planner-grid-horas">
          {horas.map((h) => (
            <div key={h} className="planner-grid-hora" style={{ height: ALTURA_HORA_PX }}>{String(h).padStart(2, '0')}:00</div>
          ))}
        </div>
        {dias.map((dia, diaIndice) => {
          const { visibles: delDia, resumenes } = porDia.get(dia) ?? { visibles: [], resumenes: [] };
          return (
            <div key={dia} className="planner-grid-columna" style={{ height: (horaFin - horaInicio) * ALTURA_HORA_PX }}>
              {horas.map((h) => <div key={h} className="planner-grid-franja" style={{ height: ALTURA_HORA_PX }} />)}
              {resumenes.map((r) => (
                <BloqueMasOts key={`mas-${String(r.grupo)}`} resumen={r} horaInicio={horaInicio}
                  desplegado={desplegados.has(dia)} controla={idDesplegado(dia)} alternar={() => alternarDia(dia)} />
              ))}
              {delDia.map((p) => (
                <EventoTile
                  key={p.evento.tipo === 'OT' ? p.evento.idOt : p.evento.identidad}
                  posicionado={p} horaInicio={horaInicio} abrir={abrir}
                  idCentro={idCentro} zonaHoraria={zonaHoraria} desde={desde} hasta={hasta} alGuardado={alGuardado}
                  dias={dias.length > 1 ? dias : undefined} diaIndice={diaIndice} gridRef={dias.length > 1 ? gridRef : undefined}
                  seleccion={seleccion} soloLectura={soloLectura}
                />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EventoTile({
  posicionado, horaInicio, abrir, idCentro, zonaHoraria, desde, hasta, alGuardado, dias, diaIndice, gridRef, seleccion, soloLectura = false,
}: {
  posicionado: EventoPosicionado; horaInicio: number; abrir: (ev: PlannerEvento) => void;
  idCentro: string; zonaHoraria: string; desde: string; hasta: string; alGuardado: () => void;
  /** Presentes sólo en Semana (>1 día visible) — brief "final gate" §1: en Día no hace falta arrastre entre columnas. */
  dias?: readonly string[]; diaIndice?: number; gridRef?: RefObject<HTMLDivElement | null>;
  seleccion?: SeleccionPlanner;
  soloLectura?: boolean;
}): ReactNode {
  const { evento, minutoInicio, minutoFin, finReales, columna, columnas } = posicionado;
  const [deltaArrastre, setDeltaArrastre] = useState(0);
  const [deltaEstiramiento, setDeltaEstiramiento] = useState(0);
  const [deltaXArrastre, setDeltaXArrastre] = useState(0);
  const [destinoVisible, setDestinoVisible] = useState<string | null>(null);
  const arrastrandoRef = useRef(false);
  const enviandoRef = useRef(false);
  //  UX-1B: confirmación del impacto de un arrastre en un diálogo accesible (antes window.confirm).
  const [pregunta, setPregunta] = useState<{ impacto: ImpactoCambio; responder: (si: boolean) => void } | null>(null);
  const confirmarImpacto = (impacto: ImpactoCambio): Promise<boolean> =>
    new Promise((resolver) => setPregunta({ impacto, responder: (si) => { setPregunta(null); resolver(si); } }));

  const top = ((minutoInicio - horaInicio * 60) / 60) * ALTURA_HORA_PX + deltaArrastre;
  const alto = Math.max(((minutoFin - minutoInicio) / 60) * ALTURA_HORA_PX + deltaEstiramiento, 18);
  const anchoPct = 100 / columnas;
  const esProyeccion = evento.tipo === 'PROYECCION';
  const numAlertas = evento.tipo === 'OT' ? evento.alertas.length : 0;
  //  UX-1C: estado legible + tono (acento lateral de la tarjeta), nunca el código crudo.
  const estadoOt = evento.tipo === 'OT' ? estadoOtLimpieza(evento.estado) : null;
  //  §12: recortado visualmente a medianoche — la duración real (nunca
  //  perdida) sigue completa en el aria-label y aquí se marca con «…».
  const recortado = finReales > minutoFin;
  //  FASE 6.7-A §Z/§N: una proyección NUNCA es arrastrable/redimensionable
  //  — no es una OT real. Una OT sigue la matriz de mutabilidad de siempre.
  const puedeMover = !soloLectura && evento.tipo === 'OT' && evento.acciones.puedeReprogramar;
  const puedeRedimensionar = !soloLectura && evento.tipo === 'OT' && evento.acciones.puedeRedimensionar;
  const puedeCruzarDias = dias !== undefined && diaIndice !== undefined && gridRef !== undefined;

  function iniciarMover(e: ReactMouseEvent): void {
    if (!puedeMover || e.button !== 0) return;
    e.preventDefault();
    const inicioX = e.clientX;
    const inicioY = e.clientY;
    //  Ancho REAL de columna, medido en el momento de arrastrar — nunca
    //  asumido (56px es el hueco de horas, `dias.length` columnas iguales
    //  tras eso, mismo `gridTemplateColumns` que pinta `VistaDiaHoraria`).
    const anchoGrid = puedeCruzarDias ? (gridRef.current?.getBoundingClientRect().width ?? 0) : 0;
    const anchoColumnaPx = puedeCruzarDias && dias.length > 0 ? Math.max(0, anchoGrid - 56) / dias.length : 0;
    let deltaMin = 0;
    let indiceDestino = diaIndice ?? 0;
    arrastrandoRef.current = false;
    function mover(ev: MouseEvent): void {
      const deltaPxY = ev.clientY - inicioY;
      const deltaPxX = ev.clientX - inicioX;
      if (Math.abs(deltaPxY) > 4 || Math.abs(deltaPxX) > 4) arrastrandoRef.current = true;
      deltaMin = minutosDesdeDeltaPx(deltaPxY);
      setDeltaArrastre((deltaMin / 60) * ALTURA_HORA_PX);
      if (puedeCruzarDias && anchoColumnaPx > 0) {
        const columnasDesplazadas = Math.round(deltaPxX / anchoColumnaPx);
        indiceDestino = Math.min(dias.length - 1, Math.max(0, (diaIndice ?? 0) + columnasDesplazadas));
        setDeltaXArrastre((indiceDestino - (diaIndice ?? 0)) * anchoColumnaPx);
      }
      const nuevoMinutoInicio = Math.max(0, minutoInicio + deltaMin);
      const hh = String(Math.floor(nuevoMinutoInicio / 60) % 24).padStart(2, '0');
      const mm = String(nuevoMinutoInicio % 60).padStart(2, '0');
      const fechaDestinoVisual = puedeCruzarDias ? dias[indiceDestino]! : evento.fecha;
      setDestinoVisible(`→ ${etiquetaDiaCorta(fechaDestinoVisual)} ${hh}:${mm}`);
    }
    async function soltar(): Promise<void> {
      document.removeEventListener('mousemove', mover);
      document.removeEventListener('mouseup', soltarSync);
      setDeltaArrastre(0);
      setDeltaXArrastre(0);
      setDestinoVisible(null);
      const fechaDestino = puedeCruzarDias ? dias[indiceDestino]! : evento.fecha;
      const huboCambioDia = fechaDestino !== evento.fecha;
      if (!arrastrandoRef.current || (deltaMin === 0 && !huboCambioDia) || evento.tipo !== 'OT' || enviandoRef.current) return;
      enviandoRef.current = true;
      const nuevoMinutoInicio = Math.max(0, minutoInicio + deltaMin);
      const hh = String(Math.floor(nuevoMinutoInicio / 60) % 24).padStart(2, '0');
      const mm = String(nuevoMinutoInicio % 60).padStart(2, '0');
      const inicioProgramadoEn = isoDeLocal(zonaHoraria, fechaDestino, `${hh}:${mm}`);
      //  `desde`/`hasta` de quien llama ya cubren toda la ventana visible
      //  (Semana = 7 días): el destino, acotado a `dias`, siempre cae dentro.
      const r = await aplicarCambioRapido(idCentro, desde, hasta, evento.idOt, { idOt: evento.idOt, inicioProgramadoEn }, { inicioProgramadoEn }, confirmarImpacto);
      enviandoRef.current = false;
      if (r.ok) alGuardado();
    }
    function soltarSync(): void { void soltar(); }
    document.addEventListener('mousemove', mover);
    document.addEventListener('mouseup', soltarSync);
  }

  function iniciarRedimensionar(e: ReactMouseEvent): void {
    if (!puedeRedimensionar || e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const inicioY = e.clientY;
    let deltaMin = 0;
    function mover(ev: MouseEvent): void {
      const deltaPx = ev.clientY - inicioY;
      deltaMin = minutosDesdeDeltaPx(deltaPx);
      setDeltaEstiramiento((deltaMin / 60) * ALTURA_HORA_PX);
    }
    async function soltar(): Promise<void> {
      document.removeEventListener('mousemove', mover);
      document.removeEventListener('mouseup', soltarSync);
      setDeltaEstiramiento(0);
      if (deltaMin === 0 || evento.tipo !== 'OT' || enviandoRef.current) return;
      const duracionDeseada = duracionDeEvento(evento) + deltaMin;
      if (duracionDeseada <= 0) return; // brief §AA: nunca duración <= 0
      enviandoRef.current = true;
      const ajusteDuracionPlanificacionMin = duracionDeseada - evento.duracionBaseMin;
      const inicioProgramadoEn = isoDeLocal(zonaHoraria, evento.fecha, inicioLocalDeEvento(evento).slice(11, 16));
      const r = await aplicarCambioRapido(
        idCentro, desde, hasta, evento.idOt, { idOt: evento.idOt, ajusteDuracionPlanificacionMin },
        { inicioProgramadoEn, ajusteDuracionPlanificacionMin }, confirmarImpacto,
      );
      enviandoRef.current = false;
      if (r.ok) alGuardado();
    }
    function soltarSync(): void { void soltar(); }
    document.addEventListener('mousemove', mover);
    document.addEventListener('mouseup', soltarSync);
  }

  const ariaEvento = `${esProyeccion ? 'Proyección' : `OT ${estadoOtLimpieza(evento.estado).label}`} — ${resumenDeEvento(evento)}, ${inicioLocalDeEvento(evento).slice(11, 16)}, ${String(duracionDeEvento(evento))} minutos${numAlertas > 0 ? `, ${String(numAlertas)} alerta(s)` : ''}`;

  //  FASE 6.7-B1: en modo selección, una OT es un checkbox real (sin drag,
  //  sin abrir detalle) — la etiqueta entera es zona de clic, sin precisión.
  if (seleccion?.activa === true && evento.tipo === 'OT') {
    const marcada = seleccion.ids.has(evento.idOt);
    return (
      <label
        className={`planner-evento seleccionable tono-${estadoOtLimpieza(evento.estado).tone}${marcada ? ' seleccionado' : ''}${numAlertas > 0 ? ' con-alerta' : ''}`}
        style={{ top, height: alto, left: `${String(columna * anchoPct)}%`, width: `calc(${String(anchoPct)}% - 3px)` }}
      >
        <input type="checkbox" className="planner-evento-check" checked={marcada} onChange={() => seleccion.alternar(evento.idOt)} aria-label={`Seleccionar ${ariaEvento}`} />
        <span className="planner-evento-hora">{inicioLocalDeEvento(evento).slice(11, 16)}</span>{' '}
        <span className="planner-evento-resumen">{resumenDeEvento(evento)}</span>
        <EstadoOt estado={evento.estado} size="sm" />
      </label>
    );
  }

  return (
    <>
    <button
      type="button"
      className={`planner-evento${esProyeccion ? ' proyectado' : ` tono-${estadoOt?.tone ?? 'neutral'}`}${numAlertas > 0 ? ' con-alerta' : ''}${puedeMover ? ' movible' : ''}`}
      style={{
        top, height: alto, left: `calc(${String(columna * anchoPct)}% + ${String(deltaXArrastre)}px)`, width: `calc(${String(anchoPct)}% - 3px)`,
        zIndex: destinoVisible !== null ? 3 : undefined,
      }}
      onMouseDown={puedeMover && seleccion?.activa !== true ? iniciarMover : undefined}
      onClick={() => {
        if (arrastrandoRef.current) { arrastrandoRef.current = false; return; } // brief §Z: un arrastre real nunca también abre el detalle
        abrir(evento);
      }}
      aria-label={ariaEvento}
    >
      {/* UX-1C (UAT): el estado va en la PRIMERA línea — en semanas densas la tarjeta mide una hora (o es una
          tira estrecha) y la tercera línea no se ve. En tarjetas estrechas (container query) se oculta antes el
          dominio y luego el texto del badge, pero su forma sigue ahí; el nombre accesible lo dice entero. */}
      <span className="planner-evento-cabeza">
        {!esProyeccion && <MarcaDominio dominio="LIMPIEZA" />}
        <span className="planner-evento-hora">{inicioLocalDeEvento(evento).slice(11, 16)}</span>
        {estadoOt !== null && <StatusBadgeEvento d={estadoOt} />}
        {numAlertas > 0 && <span aria-hidden="true" className="planner-evento-alerta" title="Conflicto o aviso">⚠</span>}
      </span>
      {esProyeccion && <span className="planner-evento-marca">Proyección · </span>}
      <span className="planner-evento-resumen">{resumenDeEvento(evento)}</span>
      {puedeRedimensionar && (
        <span
          className="planner-evento-redimensionar"
          aria-hidden="true"
          onMouseDown={iniciarRedimensionar}
          onClick={(e) => e.stopPropagation()}
        />
      )}
      {recortado && <span aria-hidden="true">…</span>}
      {/* FASE 6.7-A §Z: destino en vivo durante el arrastre — día (si cruza) y hora. */}
      {destinoVisible !== null && <span className="planner-evento-destino" aria-hidden="true">{destinoVisible}</span>}
    </button>
    {pregunta !== null && (
      <ConfirmDialog titulo={`Impacto del cambio · ${String(avisosDeImpacto(pregunta.impacto).length)} aviso(s)`} confirmar="Continuar" variante="principal"
        descripcion={(
          <ul className="lista-simple">
            {avisosDeImpacto(pregunta.impacto).map((a, k) => <li key={k}><span aria-hidden="true">{ICONO_NIVEL[a.nivel]} </span>{textoAviso(a)}</li>)}
          </ul>
        )}
        consecuencia={pregunta.impacto.numeroGlobales > 0
          ? `Hay ${String(pregunta.impacto.numeroGlobales)} avisos más en la planificación de este periodo, no causados por este cambio.` : undefined}
        alConfirmar={() => pregunta.responder(true)} cerrar={() => pregunta.responder(false)} />
    )}
    </>
  );
}

/** UX-1C — leyenda del Planner de Limpieza: el workflow completo, en su orden (sin los legados). */
export const LEYENDA_LIMPIEZA = Object.entries(ESTADOS_OT).filter(([k]) => !['ASIGNADA', 'VALIDADA', 'RECHAZADA'].includes(k)).map(([, d]) => d);

/** Badge de estado dentro de una tarjeta-botón: el nombre accesible del botón ya lo incluye. */
function StatusBadgeEvento({ d }: { d: DescEstado }): ReactNode {
  return <span aria-hidden="true" className={`etiqueta estado estado-sm tono-${d.tone}`} data-tono={d.tone}>{d.label}</span>;
}

/** UX-1D: ancho mínimo de cada «carril» (OT simultáneas) en la vista Día: hora + forma del estado legibles. */
const ANCHO_MIN_CARRIL_PX = 72;
/** En modo selección (Semana o Día) basta con que quepa el checkbox (20 px) y su marco. */
const ANCHO_MIN_CARRIL_SELECCION_PX = 32;

/** UX-1D — bloque «+N OT» de la Semana: cuántas más hay, con la forma de estado de cada una; abre el día. */
/** id del panel desplegado de un día (destino de `aria-controls` de sus bloques «+N más»). */
const idDesplegado = (fecha: string): string => `planner-dia-desplegado-${fecha}`;

/**
 * UX-1D-B — bloque «+N más» de la Semana: despliega/pliega TODAS las OT de ese día en un panel
 * bajo la cabecera (la semana crece en vertical, nunca en horizontal). Desde el panel, «Ver día».
 */
function BloqueMasOts({ resumen: r, horaInicio, desplegado, controla, alternar }: {
  resumen: ResumenSolapadas; horaInicio: number; desplegado: boolean; controla: string; alternar: () => void;
}): ReactNode {
  const top = ((r.minutoInicio - horaInicio * 60) / 60) * ALTURA_HORA_PX;
  const alto = Math.max(((r.minutoFin - r.minutoInicio) / 60) * ALTURA_HORA_PX, 18);
  const anchoPct = 100 / r.columnas;
  const ots = r.eventos.filter((e): e is EventoOt => e.tipo === 'OT');
  const resto = r.eventos.length - ots.length;
  const hora = `${String(Math.floor(r.minutoInicio / 60)).padStart(2, '0')}:${String(r.minutoInicio % 60).padStart(2, '0')}`;
  const etiqueta = `${String(r.eventos.length)} más a las ${hora}${ots.length > 0 ? ` (${ots.map((e) => `${e.codigo} ${estadoOtLimpieza(e.estado).label.toLowerCase()}`).join(', ')})` : ''}`;
  return (
    <button type="button" className={`planner-mas-ots${desplegado ? ' desplegado' : ''}`} onClick={alternar}
      aria-expanded={desplegado} aria-controls={desplegado ? controla : undefined}
      style={{ top, height: alto, left: `${String(r.columna * anchoPct)}%`, width: `calc(${String(anchoPct)}% - 3px)` }} aria-label={etiqueta} title={etiqueta}>
      <span className="planner-mas-ots-n">+{r.eventos.length} más</span>
      <span className="planner-mas-ots-formas" aria-hidden="true">
        {ots.map((e) => <span key={e.idOt} className={`etiqueta estado estado-sm tono-${estadoOtLimpieza(e.estado).tone}`} />)}
        {resto > 0 && <span className="planner-mas-ots-proy">+{resto}</span>}
      </span>
    </button>
  );
}

/** UX-1D-B — todas las OT de un día de la Semana, sin salir de ella; «Mostrar menos» y «Ver día». */
function DiaDesplegado({ id, fecha, eventos, abrir, plegar, verDia }: {
  id: string; fecha: string; eventos: readonly PlannerEvento[]; abrir: (ev: PlannerEvento) => void; plegar: () => void; verDia?: () => void;
}): ReactNode {
  const numOts = eventos.filter((e) => e.tipo === 'OT').length;
  return (
    <section id={id} className="planner-dia-desplegado" aria-label={`Todas las OT del ${etiquetaDiaLarga(fecha)}`}>
      <p className="planner-dia-desplegado-titulo">{numOts} OT · {etiquetaDiaCorta(fecha)}</p>
      <ListaEventosSimple eventos={eventos} abrir={abrir} />
      <div className="planner-dia-desplegado-acciones">
        <button type="button" className="pequeno" onClick={plegar}>Mostrar menos</button>
        {verDia !== undefined && <button type="button" className="pequeno fantasma" onClick={verDia}>Ver día</button>}
      </div>
    </section>
  );
}

/** Fila de lista compartida entre `ListaCronologica`, el día desplegado de la Semana y otros listados. */
function ListaEventosSimple({ eventos, abrir, seleccion }: { eventos: readonly PlannerEvento[]; abrir: (ev: PlannerEvento) => void; seleccion?: SeleccionPlanner }): ReactNode {
  const ordenados = [...eventos].sort((a, b) => inicioLocalDeEvento(a).localeCompare(inicioLocalDeEvento(b)));
  return (
    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
      {ordenados.map((ev) => {
        const esProyeccion = ev.tipo === 'PROYECCION';
        const numAlertas = ev.tipo === 'OT' ? ev.alertas.length : 0;
        //  FASE 6.7-B1 §V: en móvil, selección por fila completa (checkbox real), nunca drag.
        if (seleccion?.activa === true && ev.tipo === 'OT') {
          return (
            <li key={ev.idOt}>
              <label className={`planner-evento-lista seleccionable tono-${estadoOtLimpieza(ev.estado).tone}`}>
                <input type="checkbox" checked={seleccion.ids.has(ev.idOt)} onChange={() => seleccion.alternar(ev.idOt)} />
                <span className="mono pequena">{inicioLocalDeEvento(ev).slice(11, 16)}</span>
                <EstadoOt estado={ev.estado} />
                <span>{resumenDeEvento(ev)}</span>
                {numAlertas > 0 && <span aria-hidden="true">⚠</span>}
              </label>
            </li>
          );
        }
        return (
          <li key={ev.tipo === 'OT' ? ev.idOt : ev.identidad}>
            <button
              type="button"
              className={`planner-evento-lista${esProyeccion ? ' proyectado' : ` tono-${estadoOtLimpieza(ev.estado).tone}`}`}
              onClick={() => abrir(ev)}
            >
              {ev.tipo === 'OT' && <MarcaDominio dominio="LIMPIEZA" />}
              <span className="mono pequena">{inicioLocalDeEvento(ev).slice(11, 16)}</span>
              {esProyeccion && <span className="etiqueta neutra">Proyección</span>}
              {ev.tipo === 'OT' && <EstadoOt estado={ev.estado} />}
              <span>{resumenDeEvento(ev)}</span>
              {numAlertas > 0 && <span aria-hidden="true">⚠</span>}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export function ListaCronologica({ eventos, abrir, agruparPorDia = false, seleccion }: { eventos: readonly PlannerEvento[]; abrir: (ev: PlannerEvento) => void; agruparPorDia?: boolean; seleccion?: SeleccionPlanner }): ReactNode {
  const grupos = new Map<string, PlannerEvento[]>();
  for (const ev of eventos) {
    const lista = grupos.get(ev.fecha) ?? [];
    lista.push(ev);
    grupos.set(ev.fecha, lista);
  }
  return (
    <div className="planner-lista">
      {[...grupos.entries()].map(([dia, delDia]) => (
        <div key={dia} className="planner-lista-dia">
          {agruparPorDia && <h3>{etiquetaDiaLarga(dia)}</h3>}
          <ListaEventosSimple eventos={delDia} abrir={abrir} seleccion={seleccion} />
        </div>
      ))}
    </div>
  );
}

// ------------------------------------------------------------- vista timeline por operario (FASE 6.6-B · UX-1D-B)

/**
 * UX-1D-B — TIMELINE (eje X = hora del día, eje Y = operario).
 *
 * Causa raíz de la versión anterior: el cálculo (minutos → %) existía, pero
 * no había CSS: etiquetas de hora en línea («06:0007:00…»), barras en el
 * flujo (pisándose) y filas sin rejilla. Ahora:
 *   · ESCALA explícita: la pista mide como mínimo `PX_MIN_HORA_TIMELINE` (96 px)
 *     por hora (se estira si sobra ancho; si no, se desplaza DENTRO del
 *     timeline, nunca la página). OT, etiquetas y líneas de hora comparten
 *     la misma escala en % sobre esa pista.
 *   · FILAS: rejilla de 2 columnas (recurso fijo a la izquierda, `sticky`,
 *     y su pista). Cada pista es `position: relative` con su propia altura
 *     (carriles × altura): ninguna OT invade otra fila.
 *   · CARRILES automáticos para OT solapadas del mismo recurso (mismo
 *     empaquetado que Día/Semana).
 *   · Mínimo visual `ANCHO_MIN_OT_TIMELINE_PX` para OT muy cortas: la barra
 *     no miente sobre su duración (el nombre accesible dice la real). Una OT
 *     sin duración planificada se dibuja como 15 min con el borde final
 *     discontinuo y lo dice su nombre accesible.
 */
const PX_MIN_HORA_TIMELINE = 96;
const ALTURA_CARRIL_TIMELINE_PX = 40;
const ANCHO_MIN_OT_TIMELINE_PX = 44;
/** Minutos que ocupa el ancho mínimo a la escala mínima (44 px a 96 px/h ≈ 27,5 min): reparto de carriles. */
const MIN_EMPAQUETADO_TIMELINE = Math.ceil((ANCHO_MIN_OT_TIMELINE_PX / PX_MIN_HORA_TIMELINE) * 60 / 15) * 15;

/** Respeta la semántica 6.4: `minutosCapacidad===null` es "desconocido", NUNCA "0h" (brief §10). */
function textoCargaOperario(op: CargaOperarioResumen): string {
  if (op.minutosCapacidad === null) return `Carga: ${horas(op.minutosCarga)} · Disponibilidad: Sin configurar`;
  return `Carga: ${horas(op.minutosCarga)} / ${horas(op.minutosCapacidad)}`;
}

type EventoOt = Extract<PlannerEvento, { tipo: 'OT' }>;

const hhmm = (minuto: number): string => `${String(Math.floor(minuto / 60) % 24).padStart(2, '0')}:${String(minuto % 60).padStart(2, '0')}`;

/**
 * Filas: un operario = una fila (brief §2/§6). Multioperario: la MISMA OT
 * aparece en la fila de cada operario asignado — no es duplicación, es una
 * representación por recurso; el click en cualquiera abre el mismo panel
 * (misma `idOt`). §7: se usa la ventana temporal COMPLETA de la OT en cada
 * fila — el modelo (`ot_operarios.alcance` TODA_OT/LINEAS) no define una
 * duración distinta por operario/línea, así que repartirla sería inventar
 * un dato que no existe.
 *
 * UX-1D-B: la misma línea temporal en todos los anchos (en móvil se desplaza
 * dentro de su contenedor con el nombre del operario fijo a la izquierda).
 */
function VistaTimeline({
  fecha,
  eventos,
  operariosResumen,
  mostrarSinCarga,
  abrir,
  seleccion,
}: {
  fecha: string;
  eventos: readonly PlannerEvento[];
  operariosResumen: readonly CargaOperarioResumen[];
  mostrarSinCarga: boolean;
  abrir: (ev: PlannerEvento) => void;
  seleccion?: SeleccionPlanner;
}): ReactNode {
  const delDia = eventos.filter((ev) => ev.fecha === fecha);
  const { inicio: horaInicio, fin: horaFin } = rangoHorasDia(delDia);
  const numHoras = horaFin - horaInicio;
  const marcasHora = Array.from({ length: numHoras }, (_, i) => horaInicio + i);

  const idsConActividad = new Set<string>();
  for (const ev of delDia) {
    if (ev.tipo === 'OT') for (const op of ev.operarios) idsConActividad.add(op.idOperario);
  }
  const operariosAMostrar = [...(mostrarSinCarga ? operariosResumen : operariosResumen.filter((op) => idsConActividad.has(op.idOperario)))]
    .sort((a, b) => a.operarioCodigo.localeCompare(b.operarioCodigo));

  //  §5: una proyección nunca se cuelga de un operario por inferencia. Una
  //  OT materializada sin nadie asignado tampoco tiene fila propia — las
  //  dos comparten ésta, para que ninguna desaparezca del timeline.
  const eventosSinAsignar = delDia.filter((ev): boolean => ev.tipo === 'PROYECCION' || (ev.tipo === 'OT' && ev.operarios.length === 0));

  if (operariosAMostrar.length === 0 && eventosSinAsignar.length === 0) {
    return <p className="pequena suave" style={{ padding: '12px 4px' }}>Ningún operario con actividad este día.</p>;
  }

  const escala = { '--tl-horas': String(numHoras), '--tl-ancho-pista': `${String(numHoras * PX_MIN_HORA_TIMELINE)}px` } as CSSProperties;
  return (
    //  Región desplazable propia (tabIndex: se puede desplazar con teclado); la página nunca se desplaza en horizontal.
    <div className="planner-timeline-scroll" role="region" aria-label={`Timeline por operario · ${etiquetaDiaLarga(fecha)}`} tabIndex={0}>
      <div className="planner-timeline" style={escala}>
        <div className="planner-timeline-fila planner-timeline-eje-fila">
          <div className="planner-timeline-etiqueta-fila planner-timeline-esquina">Operario</div>
          <div className="planner-timeline-pista-eje" aria-hidden="true">
            {marcasHora.map((h) => (
              <span key={h} className="planner-timeline-hora" style={{ left: `${String(((h - horaInicio) / numHoras) * 100)}%` }}>
                {String(h).padStart(2, '0')}:00
              </span>
            ))}
          </div>
        </div>
        {operariosAMostrar.map((op) => (
          <FilaTimeline
            key={op.idOperario}
            recurso={op.operarioNombre}
            etiqueta={<><div className="planner-timeline-recurso">{op.operarioNombre}</div><div className="pequena suave">{textoCargaOperario(op)}</div></>}
            eventos={delDia.filter((ev): ev is EventoOt => ev.tipo === 'OT' && ev.operarios.some((o) => o.idOperario === op.idOperario))}
            horaInicio={horaInicio}
            horaFin={horaFin}
            abrir={abrir}
            seleccion={seleccion}
          />
        ))}
        {eventosSinAsignar.length > 0 && (
          <FilaTimeline
            recurso="Sin asignar"
            etiqueta={<div className="planner-timeline-recurso">Sin asignar / Proyectado</div>}
            eventos={eventosSinAsignar}
            horaInicio={horaInicio}
            horaFin={horaFin}
            abrir={abrir}
            seleccion={seleccion}
            sinAsignar
          />
        )}
      </div>
    </div>
  );
}

function FilaTimeline({
  recurso,
  etiqueta,
  eventos,
  horaInicio,
  horaFin,
  abrir,
  seleccion,
  sinAsignar = false,
}: {
  recurso: string;
  etiqueta: ReactNode;
  eventos: readonly PlannerEvento[];
  horaInicio: number;
  horaFin: number;
  abrir: (ev: PlannerEvento) => void;
  seleccion?: SeleccionPlanner;
  sinAsignar?: boolean;
}): ReactNode {
  const posicionados = posicionarEventos(eventos, MIN_EMPAQUETADO_TIMELINE);
  const carriles = posicionados.reduce((m, p) => Math.max(m, p.columnas), 1);
  return (
    <div className={`planner-timeline-fila${sinAsignar ? ' sin-asignar' : ''}`} data-carriles={carriles}>
      <div className="planner-timeline-etiqueta-fila">{etiqueta}</div>
      <div className="planner-timeline-pista" style={{ height: carriles * ALTURA_CARRIL_TIMELINE_PX + 8 }}>
        {posicionados.map((p) => (
          <EventoTileHorizontal
            key={p.evento.tipo === 'OT' ? p.evento.idOt : p.evento.identidad}
            posicionado={p}
            horaInicio={horaInicio}
            horaFin={horaFin}
            recurso={recurso}
            abrir={abrir}
            seleccion={seleccion}
          />
        ))}
      </div>
    </div>
  );
}

function EventoTileHorizontal({
  posicionado,
  horaInicio,
  horaFin,
  recurso,
  abrir,
  seleccion,
}: {
  posicionado: EventoPosicionado;
  horaInicio: number;
  horaFin: number;
  recurso: string;
  abrir: (ev: PlannerEvento) => void;
  seleccion?: SeleccionPlanner;
}): ReactNode {
  const { evento, minutoInicio, minutoFin, finReales, columna } = posicionado;
  const totalMin = (horaFin - horaInicio) * 60;
  const leftPct = ((minutoInicio - horaInicio * 60) / totalMin) * 100;
  const anchoPct = ((minutoFin - minutoInicio) / totalMin) * 100;
  const esProyeccion = evento.tipo === 'PROYECCION';
  const numAlertas = evento.tipo === 'OT' ? evento.alertas.length : 0;
  const recortado = finReales > minutoFin;
  const duracion = duracionDeEvento(evento);
  const sinDuracion = !(duracion > 0);
  const estado = evento.tipo === 'OT' ? estadoOtLimpieza(evento.estado) : null;
  const hora = hhmm(minutoInicio);
  //  F10.4: el rango dice si termina al día siguiente, en vez de cortarse a las 24:00.
  const textoDuracion = sinDuracion ? 'duración sin planificar' : `${rangoHorario(minutoInicio, duracion)}, ${String(duracion)} minutos`;
  //  Nombre accesible completo: tipo y estado, código y sala, hora, duración, recurso y avisos.
  const nombre = `${esProyeccion ? 'Proyección' : `OT ${estado!.label}`} — ${resumenDeEvento(evento)}, ${textoDuracion}, ${recurso}${numAlertas > 0 ? `, ${String(numAlertas)} alerta(s)` : ''}`;
  const estilo: CSSProperties = {
    left: `${String(leftPct)}%`,
    width: `max(calc(${String(anchoPct)}% - 2px), ${String(ANCHO_MIN_OT_TIMELINE_PX)}px)`,
    top: columna * ALTURA_CARRIL_TIMELINE_PX + 4,
    height: ALTURA_CARRIL_TIMELINE_PX - 6,
  };
  const clases = `planner-evento-h${esProyeccion ? ' proyectado' : ` tono-${estado!.tone}`}${numAlertas > 0 ? ' con-alerta' : ''}${sinDuracion ? ' sin-duracion' : ''}`;
  const contenido = (
    <>
      <span className="planner-evento-cabeza">
        <span className="planner-evento-hora">{hora}</span>
        {estado !== null && <span aria-hidden="true" className={`etiqueta estado estado-sm tono-${estado.tone}`} data-tono={estado.tone}>{estado.label}</span>}
        {numAlertas > 0 && <span aria-hidden="true" className="planner-evento-alerta" title="Conflicto o aviso">⚠</span>}
      </span>
      <span className="planner-evento-resumen">{esProyeccion && 'Proyección · '}{resumenDeEvento(evento)}{recortado && '…'}</span>
    </>
  );

  //  FASE 6.7-B1: misma OT en varias filas (multioperario) = mismo checkbox lógico.
  if (seleccion?.activa === true && evento.tipo === 'OT') {
    const marcada = seleccion.ids.has(evento.idOt);
    return (
      <label className={`${clases} seleccionable${marcada ? ' seleccionado' : ''}`} style={estilo} title={nombre}>
        <input type="checkbox" className="planner-evento-check" checked={marcada} onChange={() => seleccion.alternar(evento.idOt)} aria-label={`Seleccionar ${nombre}`} />
        {contenido}
      </label>
    );
  }

  return (
    <button type="button" className={clases} style={estilo} onClick={() => abrir(evento)} aria-label={nombre} title={nombre}>
      {contenido}
    </button>
  );
}

// ------------------------------------------------------------- vista mes

export function VistaMes({ fecha, dias, eventos, irADia, soloLectura = false }: {
  fecha: string; dias: readonly MetricasDia[]; eventos: readonly PlannerEvento[]; irADia: (fecha: string) => void;
  /** Portal cliente v2: sólo cuántas OT hay — ni carga ni capacidad (horas internas), ni avisos de planificación. */
  soloLectura?: boolean;
}): ReactNode {
  const primerDia = primerDiaMes(fecha);
  const inicioCuadricula = lunesDeSemana(primerDia);
  const ultimoDia = ultimoDiaMes(fecha);
  const finCuadricula = (() => {
    const d = new Date(`${ultimoDia}T00:00:00Z`);
    const diaIso = d.getUTCDay() === 0 ? 7 : d.getUTCDay();
    return sumarDias(ultimoDia, 7 - diaIso);
  })();
  const celdas = diasDelRango(inicioCuadricula, finCuadricula);
  const metricasPorDia = new Map(dias.map((d) => [d.fecha, d]));
  const proyectadasPorDia = new Map<string, number>();
  for (const ev of eventos) {
    if (ev.tipo === 'PROYECCION') proyectadasPorDia.set(ev.fecha, (proyectadasPorDia.get(ev.fecha) ?? 0) + 1);
  }

  return (
    <div className="calendario-anual" style={{ gridTemplateColumns: '1fr' }}>
      <div className="calendario-mes">
        <div className="calendario-mes-cabecera">
          {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map((d) => <span key={d}>{d}</span>)}
        </div>
        <div className="planner-mes-dias">
          {celdas.map((c) => {
            const fueraDeMes = c.slice(0, 7) !== fecha.slice(0, 7);
            const m = metricasPorDia.get(c);
            const proyectadas = proyectadasPorDia.get(c) ?? 0;
            return (
              <button
                key={c}
                type="button"
                className={`planner-mes-dia${fueraDeMes ? ' fuera-mes' : ''}${c === hoyIso() ? ' hoy' : ''}`}
                onClick={() => irADia(c)}
                aria-label={`${etiquetaDiaLarga(c)}${m !== undefined ? `, ${String(m.numeroOts)} OT${soloLectura ? '' : `, ${horas(m.minutosCarga)}`}` : ''}`}
              >
                <span className="planner-mes-dia-numero">{Number(c.slice(8, 10))}</span>
                {!fueraDeMes && m !== undefined && (m.numeroOts > 0 || proyectadas > 0) && (
                  <span className="planner-mes-dia-resumen">
                    {m.numeroOts > 0 && <span>{m.numeroOts} OT</span>}
                    {proyectadas > 0 && <span className="suave">+{proyectadas} proy.</span>}
                    {!soloLectura && m.minutosCarga > 0 && (
                      <span>{horas(m.minutosCarga)} / {horasOSinConfigurar(m.minutosCapacidad, m.operariosSinConfigurar)}</span>
                    )}
                    {!soloLectura && m.numeroAlertas > 0 && <span aria-hidden="true">⚠ {m.numeroAlertas}</span>}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------- bandeja pendiente

/**
 * FASE 6.7-A §O: OTs que necesitan atención — VISTA DERIVADA, nunca un
 * estado nuevo en base de datos. Se calcula filtrando `eventos` (que ya
 * trae sus alertas de FASE 6.4) por los tipos que el brief marca como
 * candidatos: sin asignar, vencida, conflictos (solapamiento/bloqueo),
 * falta disponibilidad configurada, no aptitud. Clic enfoca la MISMA OT en
 * el mismo panel de detalle — nunca una vista paralela.
 */
const TIPOS_ALERTA_BANDEJA = new Set<string>([
  'OT_SIN_ASIGNAR', 'OT_VENCIDA', 'SOLAPAMIENTO', 'BLOQUEO', 'FUERA_DISPONIBILIDAD', 'DISPONIBILIDAD_SIN_CONFIGURAR', 'OPERARIO_NO_APTO',
]);

/** F10.0: el código interno de la alerta nunca se muestra tal cual. */
const ETIQUETA_ALERTA: Readonly<Record<string, string>> = {
  OT_SIN_ASIGNAR: 'Sin asignar',
  OT_VENCIDA: 'Vencida',
  SOLAPAMIENTO: 'Solapamiento',
  BLOQUEO: 'Bloqueo',
  FUERA_DISPONIBILIDAD: 'Fuera de disponibilidad',
  DISPONIBILIDAD_SIN_CONFIGURAR: 'Disponibilidad sin configurar',
  OPERARIO_NO_APTO: 'Operario no apto',
};

function BandejaPendiente({ eventos, abrir }: { eventos: readonly PlannerEvento[]; abrir: (ev: PlannerEvento) => void }): ReactNode {
  const [abierto, setAbierto] = useState(false);
  const pendientes = eventos.filter((ev): ev is PlannerEventoMaterializado =>
    ev.tipo === 'OT' && ev.alertas.some((a) => TIPOS_ALERTA_BANDEJA.has(a.tipo)),
  );
  if (pendientes.length === 0) return null;
  return (
    <div className="panel" style={{ marginTop: 16 }}>
      <div className="panel-cabecera">
        <h3>Pendientes de atención ({pendientes.length})</h3>
        <button type="button" className="enlace" onClick={() => setAbierto((v) => !v)} aria-expanded={abierto}>
          {abierto ? 'Ocultar' : 'Ver'}
        </button>
      </div>
      {abierto && (
        <ul className="panel-cuerpo" style={{ listStyle: 'none', margin: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {pendientes.map((ev) => (
            <li key={ev.idOt}>
              <button type="button" className="enlace fila" style={{ gap: 8, width: '100%', justifyContent: 'flex-start' }} onClick={() => abrir(ev)}>
                <span className="mono">{ev.fecha}</span>
                <span>{ev.codigo} · {ev.codigoSala}</span>
                <span className="suave pequena">{ev.alertas.filter((a) => TIPOS_ALERTA_BANDEJA.has(a.tipo)).map((a) => ETIQUETA_ALERTA[a.tipo] ?? a.tipo).filter((t, i, ts) => ts.indexOf(t) === i).join(', ')}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ------------------------------------------------------------- panel operativo (pre-F14.2)

/** «88,6 %» — un decimal, coma decimal; sin tareas, «—» (nunca una división por cero). */
export function porcentajeTareas(n: number, total: number): string {
  return total === 0 ? '—' : `${(Math.round((n * 1000) / total) / 10).toFixed(1).replace('.', ',')} %`;
}

const ETIQUETA_ESTADO_NC: Record<string, string> = {
  ABIERTA: 'abierta', EN_TRATAMIENTO: 'en tratamiento', CERRADA: 'cerrada', ANULADA: 'anulada',
};

/**
 * Pre-F14.2 — panel operativo del Planner. Las cifras vienen YA calculadas
 * en la misma respuesta del Planner (`planner.panel`), sobre exactamente las
 * OT que se pintan con los filtros y el periodo actuales: no hay una segunda
 * consulta que pueda dar otra cosa. El detalle NO_OK es la lista que produjo
 * el contador (`tareas.noOk === lineasNoOk.length`). El selector Día/Semana/
 * Mes ES la vista del Planner (Timeline es un día): cambiarlo cambia lo que
 * se ve arriba y lo que se cuenta aquí a la vez.
 *
 * PC-v2.1-A: dos bloques que no se mezclan — «Órdenes de trabajo» (situaciones excluyentes que suman el total,
 * e indicadores transversales aparte) y «Tareas» (ejecutadas / no aplica / pendientes, y verificadas y NO_OK).
 */
function PanelOperativo({
  panel, vista, desde, hasta, abierto, alternar, detalleNoOk, alternarNoOk, fijarVista, volver, rutaExportar,
}: {
  panel: PanelPlanner; vista: Vista; desde: string; hasta: string; abierto: boolean; alternar: () => void;
  detalleNoOk: boolean; alternarNoOk: () => void; fijarVista: (v: Vista) => void; volver: { a: string; texto: string };
  /** PC-v2.2-C2.2: el Excel del panel, con el centro, el periodo y los filtros actuales; null sin lectura analítica. */
  rutaExportar: string | null;
}): ReactNode {
  const t = panel.tareas;
  const o = panel.ots;
  const periodo = vista === 'mes' ? 'mes' : vista === 'semana' ? 'semana' : 'dia';
  const etiquetaPeriodo = desde === hasta ? etiquetaDiaLarga(desde) : `${etiquetaDiaCorta(desde)} – ${etiquetaDiaCorta(hasta)}`;
  return (
    <section className="panel panel-operativo" aria-labelledby="panel-operativo-titulo" style={{ marginBottom: 16 }}>
      <div className="panel-cabecera">
        <h2 id="panel-operativo-titulo" className="titulo-panel-operativo">Panel operativo</h2>
        {!abierto && (
          <span className="pequena suave resumen-panel-operativo" data-testid="resumen-panel-operativo">
            {o.total} OT · {t.total} tareas · {porcentajeTareas(t.ejecutadas, t.total)} ejecutadas · {porcentajeTareas(t.verificadas, t.total)} verificadas · {t.noOk} NO_OK
          </span>
        )}
        <button type="button" className="enlace" onClick={alternar} aria-expanded={abierto} aria-controls="panel-operativo-cuerpo">
          {abierto ? 'Plegar' : 'Desplegar'}
        </button>
      </div>
      {abierto && (
        <div className="panel-cuerpo" id="panel-operativo-cuerpo">
          <div className="fila" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
            <nav className="fila" style={{ gap: 4 }} aria-label="Periodo del panel">
              {(['dia', 'semana', 'mes'] as const).map((v) => (
                <button key={v} type="button" className={periodo === v ? 'principal' : 'secundario'} aria-pressed={periodo === v} onClick={() => fijarVista(v)}>
                  {v === 'dia' ? 'Día' : v === 'semana' ? 'Semana' : 'Mes'}
                </button>
              ))}
            </nav>
            <span className="pequena suave">{etiquetaPeriodo} · {panel.otIncluidas} OT con los filtros actuales</span>
            {rutaExportar !== null && <BotonExportarExcel ruta={rutaExportar} />}
          </div>
          <div className="panel-operativo-bloques">
            <div className="panel-operativo-bloque">
              <h3 className="titulo-bloque-panel">Órdenes de trabajo</h3>
              <table className="kpi-limpieza kpi-panel-operativo" data-testid="panel-ots">
                <caption className="sr-solo">Órdenes de trabajo del periodo y los filtros actuales</caption>
                <tbody>
                  <tr><th scope="row">OT (sin anuladas)</th><td className="cifra-kpi">{o.total}</td><td /></tr>
                  {situacionesOt(o).map((c) => (
                    <tr key={c.clave}><th scope="row">{c.texto}</th><td className="cifra-kpi">{c.valor}</td><td className="cifra-kpi">{porcentajeTareas(c.valor, o.total)}</td></tr>
                  ))}
                </tbody>
                <tbody className="kpi-transversales">
                  {indicadoresOt(o).map((c) => (
                    <tr key={c.clave}><th scope="row">{c.texto}</th><td className="cifra-kpi">{c.valor}</td><td className="cifra-kpi">{porcentajeTareas(c.valor, o.total)}</td></tr>
                  ))}
                </tbody>
              </table>
              {/* PC-v2.2-C2.1 — G5, el mismo del Calendario del cliente; la tabla de arriba es su versión en cifras. */}
              {o.total > 0 && <div className="panel-operativo-grafico"><GraficoSituacionesOt o={o} /></div>}
              <p className="pequena tenue" style={{ marginBottom: 0 }}>
                Cada OT está en una sola situación: suman el total. «Con incidencia» y «Fuera de plazo» se solapan con ellas y no se suman.
                {o.anuladas > 0 && ` ${String(o.anuladas)} anulada(s) se pintan, pero no cuentan.`}
              </p>
            </div>
            <div className="panel-operativo-bloque">
              <h3 className="titulo-bloque-panel">Tareas</h3>
              <table className="kpi-limpieza kpi-panel-operativo" data-testid="panel-tareas">
                <caption className="sr-solo">Tareas de las OT del periodo y los filtros actuales</caption>
                <tbody>
                  <tr><th scope="row">Tareas totales</th><td className="cifra-kpi">{t.total}</td><td /></tr>
                  <tr><th scope="row">Ejecutadas</th><td className="cifra-kpi">{t.ejecutadas}</td><td className="cifra-kpi">{porcentajeTareas(t.ejecutadas, t.total)}</td></tr>
                  <tr><th scope="row">No aplica</th><td className="cifra-kpi">{t.noAplica}</td><td className="cifra-kpi">{porcentajeTareas(t.noAplica, t.total)}</td></tr>
                  <tr><th scope="row">Pendientes</th><td className="cifra-kpi">{t.pendientes}</td><td className="cifra-kpi">{porcentajeTareas(t.pendientes, t.total)}</td></tr>
                  <tr><th scope="row">Verificadas</th><td className="cifra-kpi">{t.verificadas}</td><td className="cifra-kpi">{porcentajeTareas(t.verificadas, t.total)}</td></tr>
                  <tr>
                    <th scope="row">NO_OK</th>
                    <td className="cifra-kpi">
                      <button
                        type="button" className="enlace kpi-no-ok" onClick={alternarNoOk} aria-expanded={detalleNoOk} aria-controls="detalle-no-ok"
                        aria-label={`NO_OK: ${String(t.noOk)} tarea(s). ${detalleNoOk ? 'Ocultar' : 'Ver'} el detalle`}
                      >
                        {t.noOk}
                      </button>
                    </td>
                    <td className="cifra-kpi">{porcentajeTareas(t.noOk, t.total)}</td>
                  </tr>
                </tbody>
              </table>
              <p className="pequena tenue" style={{ marginBottom: 0 }}>
                Ejecutadas: el trabajo se hizo (confirmada, verificada o NO_OK). No aplica: no había nada que hacer, no es trabajo ejecutado. Ejecutadas + no aplica + pendientes = total. Verificadas y NO_OK son parte de las ejecutadas. Las proyecciones sin OT no cuentan.
              </p>
            </div>
          </div>
          {detalleNoOk && <DetalleNoOk lineas={panel.lineasNoOk} volver={volver} />}
        </div>
      )}
    </section>
  );
}

function DetalleNoOk({ lineas, volver }: { lineas: PanelPlanner['lineasNoOk']; volver: { a: string; texto: string } }): ReactNode {
  return (
    <div id="detalle-no-ok" className="sep" role="region" aria-label="Tareas NO_OK">
      <h3 style={{ marginTop: 0 }}>Tareas NO_OK ({lineas.length})</h3>
      {lineas.length === 0 ? (
        <p className="pequena tenue">No hay tareas NO_OK con los filtros actuales.</p>
      ) : (
        <div className="tabla-desliza">
          <table className="tabla-tarjetas">
            <thead>
              <tr><th>Fecha</th><th>Cliente / centro</th><th>Ubicación</th><th>Tarea</th><th>OT</th><th>Estado / resolución</th></tr>
            </thead>
            <tbody>
              {lineas.map((l) => (
                <tr key={l.idLinea} data-testid="fila-no-ok">
                  <td data-label="Fecha">{fecha(l.fecha)}</td>
                  <td data-label="Cliente / centro">{l.cliente} · {l.centro}</td>
                  <td data-label="Ubicación">{l.ubicacion}</td>
                  <td data-label="Tarea" className="celda-titulo">{l.tarea}</td>
                  <td data-label="OT">
                    <Link to={`/ots/${l.idOt}?linea=${l.idLinea}`} state={{ volver }} aria-label={`Abrir ${l.codigoOt}${l.orden > 0 ? `, línea ${String(l.orden)}` : ''}`}>
                      {l.codigoOt}
                    </Link>
                    {l.orden > 0 && <span className="pequena tenue"> · línea {l.orden}</span>}
                  </td>
                  <td data-label="Estado / resolución">
                    <span className="etiqueta error">NO_OK</span>
                    {l.motivo !== null && <span className="pequena"> {l.motivo}</span>}
                    <div className="pequena">
                      {l.resolucion === null ? (
                        <span className="tenue">Sin NC que la cubra</span>
                      ) : (
                        <Link to={`/calidad/no-conformidades/${l.resolucion.idNc}`} state={{ volver }}>
                          {l.resolucion.codigoNc} · {ETIQUETA_ESTADO_NC[l.resolucion.estadoNc] ?? l.resolucion.estadoNc}
                        </Link>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------- alertas y capacidad

function PanelAlertas({ alertas, abierto, alternar }: { alertas: readonly AlertaPlanificacion[]; abierto: boolean; alternar: () => void }): ReactNode {
  const porSeveridad = { ALTA: 0, AVISO: 0, INFO: 0 };
  for (const a of alertas) porSeveridad[a.severidad] += 1;
  return (
    <div className="panel" style={{ marginTop: 16 }}>
      <div className="panel-cabecera">
        <h3>Alertas ({alertas.length})</h3>
        <button type="button" className="enlace" onClick={alternar} aria-expanded={abierto}>
          {abierto ? 'Ocultar' : 'Ver todas'}
        </button>
      </div>
      {!abierto && (
        <div className="panel-cuerpo pequena suave">
          {porSeveridad.ALTA > 0 && `${String(porSeveridad.ALTA)} alta(s) · `}
          {porSeveridad.AVISO > 0 && `${String(porSeveridad.AVISO)} aviso(s) · `}
          {porSeveridad.INFO > 0 && `${String(porSeveridad.INFO)} informativa(s)`}
        </div>
      )}
      {abierto && (
        <ul className="panel-cuerpo" style={{ listStyle: 'none', margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {alertas.map((a, i) => (
            <li key={i} className="fila pequena" style={{ gap: 8 }}>
              <span className={`etiqueta ${a.severidad === 'ALTA' ? 'error' : a.severidad === 'AVISO' ? 'aviso' : 'info'}`}>{a.severidad}</span>
              <span>{a.mensaje}</span>
              {a.codigoOt !== undefined && <span className="cod pequena">{a.codigoOt}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ------------------------------------------------------------- paneles de detalle

/**
 * FASE 6.7 (UAT): IMPACTO DEL CAMBIO, no el contexto del Planner. El
 * backend ya separa lo causado/relacionado con esta OT (deduplicado, con
 * nivel) del resto de avisos del periodo, que sólo se cuentan.
 */
const ICONO_NIVEL = { BLOQUEANTE: '⛔', AVISO: '⚠', INFORMACION: 'ℹ' } as const;
const TEXTO_NIVEL = { BLOQUEANTE: 'Bloqueante', AVISO: 'Aviso', INFORMACION: 'Información' } as const;
function avisosDeImpacto(i: ImpactoCambio): ImpactoCambio['directos'] {
  return [...i.directos, ...i.relacionados];
}
/** ¿Hay algo que el usuario deba ver antes de guardar? */
function hayImpacto(i: ImpactoCambio): boolean {
  return avisosDeImpacto(i).length > 0;
}
/** ¿Hay algo más que información (que merezca parar el gesto de arrastre)? */
function requiereConfirmar(i: ImpactoCambio): boolean {
  return avisosDeImpacto(i).some((a) => a.nivel !== 'INFORMACION');
}
function bloquea(i: ImpactoCambio): boolean {
  return avisosDeImpacto(i).some((a) => a.nivel === 'BLOQUEANTE');
}
function textoAviso(a: ImpactoCambio['directos'][number]): string {
  //  Los mensajes del motor 6.4 no nombran al operario: se añaden (todos, si el aviso agrupaba varios).
  const quien = a.operarios.length > 0 ? ` (${a.operarios.map((o) => o.nombre).join(', ')})` : '';
  return `${a.mensaje}${quien}`;
}
function etiquetaConfirmar(i: ImpactoCambio | null, normal: string): string {
  if (i === null) return normal;
  return requiereConfirmar(i) ? 'Confirmar de todas formas' : normal;
}

function BloqueImpacto({ impacto, verAvisosPlanner }: { impacto: ImpactoCambio; verAvisosPlanner?: () => void }): ReactNode {
  const avisos = avisosDeImpacto(impacto);
  return (
    <section className={`nota ${bloquea(impacto) ? 'error' : requiereConfirmar(impacto) ? 'aviso' : 'info'}`} role="alert" aria-label="Impacto del cambio" style={{ marginTop: 8 }}>
      <p><strong>Impacto del cambio</strong> · {avisos.length === 1 ? '1 aviso' : `${String(avisos.length)} avisos`}</p>
      <ul style={{ margin: '4px 0', listStyle: 'none', paddingLeft: 0 }}>
        {avisos.map((a, i) => (
          <li key={i}>
            <span aria-hidden="true">{ICONO_NIVEL[a.nivel]} </span>
            <span className="sr-solo">{TEXTO_NIVEL[a.nivel]}: </span>
            {textoAviso(a)}
          </li>
        ))}
      </ul>
      {impacto.numeroGlobales > 0 && (
        <p className="pequena">
          Hay {impacto.numeroGlobales === 1 ? '1 aviso adicional' : `${String(impacto.numeroGlobales)} avisos adicionales`} en la planificación de este periodo, no causados por este cambio.{' '}
          {verAvisosPlanner !== undefined && <button type="button" className="enlace" onClick={verAvisosPlanner}>Ver avisos del Planner</button>}
        </p>
      )}
      {bloquea(impacto)
        ? <p className="pequena">Esta OT ya no admite este cambio.</p>
        : <p className="pequena">Pulsa de nuevo para guardar, o cancela.</p>}
    </section>
  );
}

/** FASE 6.7-A §H: siempre reutiliza el mismo preview — nunca autoriza, sólo informa antes de confirmar. */
async function previsualizarCambio(
  idCentro: string, desde: string, hasta: string, cambio: CambioHipoteticoOt,
): Promise<ImpactoCambio> {
  const r = await api.post<ResultadoPreviewCambio>(`/api/centros/${idCentro}/planificacion/preview-cambio`, { desde, hasta, cambios: [cambio] });
  return r.impacto;
}

/**
 * FASE 6.7-A §C/§F/§Z: camino RÁPIDO de drag/resize — mismo preview, mismo
 * PATCH .../reprogramar de siempre. Sin avisos, aplica al soltar (fluido,
 * "optimista" sólo en el sentido de no repetir el formulario). Con avisos,
 * pregunta con `confirmar` (UX-1B: un ConfirmDialog accesible del propio
 * evento; antes `window.confirm`); la alternativa por teclado
 * (Reprogramar/Ajustar duración) sigue confirmando dentro de su panel.
 * Nunca actualiza la UI como éxito antes de la respuesta del backend
 * (brief §C): quien llama sólo refresca (`alGuardado`) tras el `await`.
 */
async function aplicarCambioRapido(
  idCentro: string, desde: string, hasta: string, idOt: string, cambio: CambioHipoteticoOt, cuerpoPatch: { inicioProgramadoEn: string; ajusteDuracionPlanificacionMin?: number },
  confirmar: (impacto: ImpactoCambio) => Promise<boolean>,
): Promise<{ ok: true } | { ok: false; error: unknown }> {
  try {
    const impacto = await previsualizarCambio(idCentro, desde, hasta, cambio);
    //  FASE 6.7 (UAT): sólo se interrumpe el gesto si el CAMBIO causa algo
    //  más que información — nunca por los avisos generales de la semana.
    if (requiereConfirmar(impacto) && !(await confirmar(impacto))) {
      return { ok: false, error: null };
    }
    await api.patch(`/api/ots/${idOt}/reprogramar`, cuerpoPatch);
    return { ok: true };
  } catch (error) {
    return { ok: false, error };
  }
}

function FormularioReprogramar({
  idCentro, evento, zonaHoraria, alGuardado, alCancelar, verAvisosPlanner,
}: {
  idCentro: string; evento: PlannerEventoMaterializado; zonaHoraria: string; alGuardado: () => void; alCancelar: () => void;
  verAvisosPlanner?: () => void;
}): ReactNode {
  const inicial = evento.inicioLocal !== null
    ? { fecha: evento.inicioLocal.slice(0, 10), hora: evento.inicioLocal.slice(11, 16) }
    : { fecha: evento.fecha, hora: '09:00' };
  const [fecha, setFecha] = useState(inicial.fecha);
  const [hora, setHora] = useState(inicial.hora);
  const [avisos, setAvisos] = useState<ImpactoCambio | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  function cambiar(f: () => void): void {
    f();
    setAvisos(null); // un valor distinto invalida los avisos ya vistos
  }

  async function guardar(): Promise<void> {
    setError(null);
    setEnviando(true);
    try {
      const inicioProgramadoEn = isoDeLocal(zonaHoraria, fecha, hora);
      if (avisos === null) {
        const desde = fecha < evento.fecha ? fecha : evento.fecha;
        const hasta = fecha > evento.fecha ? fecha : evento.fecha;
        const encontrados = await previsualizarCambio(idCentro, desde, hasta, { idOt: evento.idOt, inicioProgramadoEn });
        if (hayImpacto(encontrados)) {
          setAvisos(encontrados);
          return;
        }
      }
      await api.patch(`/api/ots/${evento.idOt}/reprogramar`, { inicioProgramadoEn });
      alGuardado();
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="panel accion-linea">
      <div className="panel-cabecera"><h3>Reprogramar</h3><button type="button" className="pequeno" onClick={alCancelar} disabled={enviando}>Cancelar</button></div>
      <div className="panel-cuerpo">
        {error !== null && <ErrorCaja error={error} />}
        <div className="fila" style={{ gap: 12 }}>
          <div className="campo">
            <label htmlFor="reprogramar-fecha">Fecha</label>
            <input id="reprogramar-fecha" type="date" value={fecha} onChange={(e) => cambiar(() => setFecha(e.target.value))} disabled={enviando} />
          </div>
          <div className="campo">
            <label htmlFor="reprogramar-hora">Hora</label>
            <input id="reprogramar-hora" type="time" value={hora} onChange={(e) => cambiar(() => setHora(e.target.value))} disabled={enviando} />
          </div>
        </div>
        {avisos !== null && <BloqueImpacto impacto={avisos} verAvisosPlanner={verAvisosPlanner} />}
        <button type="button" className="principal" disabled={enviando || (avisos !== null && bloquea(avisos))} onClick={() => void guardar()}>
          {enviando ? 'Guardando…' : etiquetaConfirmar(avisos, 'Guardar')}
        </button>
      </div>
    </div>
  );
}

function FormularioAjustarDuracion({
  idCentro, evento, zonaHoraria, alGuardado, alCancelar, verAvisosPlanner,
}: {
  idCentro: string; evento: PlannerEventoMaterializado; zonaHoraria: string; alGuardado: () => void; alCancelar: () => void;
  verAvisosPlanner?: () => void;
}): ReactNode {
  const [minutos, setMinutos] = useState(evento.duracionPlanificadaMin);
  const [avisos, setAvisos] = useState<ImpactoCambio | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function guardar(): Promise<void> {
    if (!Number.isInteger(minutos) || minutos <= 0) {
      setError(new Error('La duración tiene que ser un número entero de minutos mayor que cero.'));
      return;
    }
    setError(null);
    setEnviando(true);
    try {
      const ajusteDuracionPlanificacionMin = minutos - evento.duracionBaseMin;
      //  Mismo inicio de siempre: `PATCH .../reprogramar` exige `inicioProgramadoEn`
      //  aunque sólo se toque la duración (brief FASE F: reutilizar el mismo endpoint).
      const inicioProgramadoEn = evento.inicioLocal !== null
        ? isoDeLocal(zonaHoraria, evento.inicioLocal.slice(0, 10), evento.inicioLocal.slice(11, 16))
        : null;
      if (inicioProgramadoEn === null) {
        setError(new Error('Esta OT no tiene un inicio programado conocido: no se puede ajustar la duración desde aquí.'));
        return;
      }
      if (avisos === null) {
        const encontrados = await previsualizarCambio(idCentro, evento.fecha, evento.fecha, { idOt: evento.idOt, ajusteDuracionPlanificacionMin });
        if (hayImpacto(encontrados)) {
          setAvisos(encontrados);
          return;
        }
      }
      await api.patch(`/api/ots/${evento.idOt}/reprogramar`, { inicioProgramadoEn, ajusteDuracionPlanificacionMin });
      alGuardado();
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="panel accion-linea">
      <div className="panel-cabecera"><h3>Ajustar duración</h3><button type="button" className="pequeno" onClick={alCancelar} disabled={enviando}>Cancelar</button></div>
      <div className="panel-cuerpo">
        {error !== null && <ErrorCaja error={error} />}
        <div className="campo">
          <label htmlFor="ajustar-duracion-minutos">Duración (minutos)</label>
          <input
            id="ajustar-duracion-minutos" type="number" min={1} step={1} value={minutos}
            onChange={(e) => { setMinutos(Number(e.target.value)); setAvisos(null); }}
            disabled={enviando}
          />
        </div>
        {avisos !== null && <BloqueImpacto impacto={avisos} verAvisosPlanner={verAvisosPlanner} />}
        <button type="button" className="principal" disabled={enviando || (avisos !== null && bloquea(avisos))} onClick={() => void guardar()}>
          {enviando ? 'Guardando…' : etiquetaConfirmar(avisos, 'Guardar')}
        </button>
      </div>
    </div>
  );
}

function FormularioAsignaciones({
  idCentro, evento, acciones, alGuardado, alCancelar, verAvisosPlanner,
}: {
  idCentro: string; evento: PlannerEventoMaterializado; acciones: AccionesPlannerOt; alGuardado: () => void; alCancelar: () => void;
  verAvisosPlanner?: () => void;
}): ReactNode {
  const operariosCentro = useDatos(
    () => api.get<{ operarios: { id: string; codigo: string; nombre: string; apellidos: string; activo: boolean }[] }>(`/api/operarios?idCentro=${idCentro}&activo=true&dominio=LIMPIEZA`),
    [idCentro],
  );
  const [idOperarioNuevo, setIdOperarioNuevo] = useState('');
  const [avisos, setAvisos] = useState<ImpactoCambio | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [pendiente, setPendiente] = useState<{ operacion: 'ANADIR' | 'RETIRAR'; idOperario: string } | null>(null);

  const yaAsignados = new Set(evento.operarios.map((o) => o.idOperario));
  const disponibles = (operariosCentro.datos?.operarios ?? []).filter((o) => !yaAsignados.has(o.id));

  async function aplicar(operacion: 'ANADIR' | 'RETIRAR', idOperario: string): Promise<void> {
    setError(null);
    setEnviando(true);
    try {
      if (avisos === null || pendiente?.idOperario !== idOperario || pendiente.operacion !== operacion) {
        const cambio =
          operacion === 'ANADIR'
            ? { idOt: evento.idOt, operariosAnadir: [idOperario] }
            : { idOt: evento.idOt, operariosQuitar: [idOperario] };
        const encontrados = await previsualizarCambio(idCentro, evento.fecha, evento.fecha, cambio);
        if (hayImpacto(encontrados)) {
          setAvisos(encontrados);
          setPendiente({ operacion, idOperario });
          return;
        }
      }
      await api.post(`/api/ots/${evento.idOt}/asignaciones`, {
        operacion, alcance: 'TODA_OT', idsOperarios: [idOperario], idsLineas: [],
      });
      setAvisos(null);
      setPendiente(null);
      setIdOperarioNuevo('');
      alGuardado();
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="panel accion-linea">
      <div className="panel-cabecera"><h3>Asignaciones</h3><button type="button" className="pequeno" onClick={alCancelar} disabled={enviando}>Cancelar</button></div>
      <div className="panel-cuerpo">
        {error !== null && <ErrorCaja error={error} />}
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {evento.operarios.length === 0 && <li className="suave pequena">Sin operarios asignados.</li>}
          {evento.operarios.map((op) => (
            <li key={op.idOperario} className="fila" style={{ gap: 8, justifyContent: 'space-between' }}>
              <span>{op.nombre} ({op.codigo})</span>
              {/* FASE 6.7-B1: el Planner sólo quita a nivel de toda la OT; sobre una
                  asignación por líneas, RETIRAR TODA_OT no haría nada — se explica
                  en vez de ofrecer un botón que parecería funcionar. */}
              {acciones.puedeDesasignar && op.alcance === 'LINEAS' && (
                <span className="pequena suave">Asignado por líneas: retíralo desde la ficha de la OT.</span>
              )}
              {acciones.puedeDesasignar && op.alcance !== 'LINEAS' && (
                <button type="button" className="pequeno peligro" disabled={enviando} onClick={() => void aplicar('RETIRAR', op.idOperario)}>
                  {pendiente?.idOperario === op.idOperario && pendiente.operacion === 'RETIRAR' && avisos !== null ? 'Confirmar quitar' : 'Quitar'}
                </button>
              )}
            </li>
          ))}
        </ul>
        {acciones.puedeAsignar && (
          <div className="fila" style={{ gap: 8, marginTop: 8 }}>
            <label className="sr-solo" htmlFor="asignar-operario-nuevo">Añadir operario</label>
            <select id="asignar-operario-nuevo" value={idOperarioNuevo} onChange={(e) => { setIdOperarioNuevo(e.target.value); setAvisos(null); }} disabled={enviando}>
              <option value="">Añadir operario…</option>
              {disponibles.map((o) => <option key={o.id} value={o.id}>{o.nombre} {o.apellidos} ({o.codigo})</option>)}
            </select>
            <button type="button" className="secundario" disabled={enviando || idOperarioNuevo === ''} onClick={() => void aplicar('ANADIR', idOperarioNuevo)}>
              {pendiente?.idOperario === idOperarioNuevo && pendiente.operacion === 'ANADIR' && avisos !== null ? 'Confirmar añadir' : 'Añadir'}
            </button>
          </div>
        )}
        {avisos !== null && <BloqueImpacto impacto={avisos} verAvisosPlanner={verAvisosPlanner} />}
      </div>
    </div>
  );
}

function PanelDetalleOt({
  evento, zonaHoraria, cerrar, alGuardado, verAvisosPlanner,
}: {
  evento: PlannerEventoMaterializado; zonaHoraria: string; cerrar: () => void; alGuardado: () => void; verAvisosPlanner?: () => void;
}): ReactNode {
  const idOt = evento.idOt;
  const ubicacion = useLocation();
  const ot = useDatos(() => api.get<OtDetalle>(`/api/ots/${idOt}`), [idOt]);
  const [accion, setAccion] = useState<'ninguna' | 'reprogramar' | 'duracion' | 'asignar'>('ninguna');
  const atencion = atencionDeOt(evento);
  const { puede } = useSesion();
  //  `evento.acciones` decide si la OT en sí lo admite (estado); `puede`
  //  decide si ESTE usuario tiene el permiso — ambas cosas hacen falta
  //  para ofrecer el botón (el backend vuelve a exigir las dos al aplicar).
  const acciones = {
    ...evento.acciones,
    puedeReprogramar: evento.acciones.puedeReprogramar && puede('ots:reprogramar'),
    puedeRedimensionar: evento.acciones.puedeRedimensionar && puede('ots:reprogramar'),
    puedeAsignar: evento.acciones.puedeAsignar && puede('ots:asignar'),
    puedeDesasignar: evento.acciones.puedeDesasignar && puede('ots:asignar'),
  };
  const idCentro = ot.datos?.idCentro ?? '';

  function alternar(a: typeof accion): void {
    setAccion((actual) => (actual === a ? 'ninguna' : a));
  }
  function guardadoDesdeAccion(): void {
    setAccion('ninguna');
    alGuardado();
  }

  return (
    <Modal titulo={ot.datos?.codigo ?? evento.codigo} cerrar={cerrar} pie={
      <>
        <span className="modal-pie-relleno" />
        {ot.datos !== null && (
          <Link className="principal boton" to={`/ots/${idOt}`} state={{ volver: { a: `${ubicacion.pathname}${ubicacion.search}`, texto: 'Planner', state: ubicacion.state } }}>
            Abrir OT
          </Link>
        )}
      </>
    }>
      {ot.cargando && ot.datos === null && <Cargando />}
      {ot.error !== null && <ErrorCaja error={ot.error} />}
      {ot.datos !== null && (
        <div className="datos">
          <div className="dato"><div className="etiqueta-dato">Estado</div><div className="valor-dato"><EstadoOt estado={ot.datos.estado} /></div></div>
          <div className="dato"><div className="etiqueta-dato">Fecha</div><div className="valor-dato">{fecha(evento.fecha)} · {horarioDeOt(evento)}</div></div>
          {/*  F10.1: lo necesario para entender la OT sin abrir la ficha — avance, quién y qué pide atención. */}
          <div className="dato"><div className="etiqueta-dato">Avance</div><div className="valor-dato">{textoAvance(evento)}</div></div>
          {/*  Sin asignados no se repite aquí: ya lo dice «Atención». */}
          {evento.operarios.length > 0 && (
            <div className="dato"><div className="etiqueta-dato">Asignados</div><div className="valor-dato">{nombresOperarios(evento.operarios, evento.operarios.length)}</div></div>
          )}
          {atencion.length > 0 && (
            <div className="dato"><div className="etiqueta-dato">Atención</div><div className="valor-dato fila" style={{ gap: 6, flexWrap: 'wrap' }}>
              {atencion.map((a) => <span key={a.clave} className={`etiqueta ${a.tono}`}>{a.texto}</span>)}
            </div></div>
          )}
          <div className="dato"><div className="etiqueta-dato">Centro</div><div className="valor-dato">{ot.datos.centroDescripcion}</div></div>
          <div className="dato"><div className="etiqueta-dato">Sala</div><div className="valor-dato">{ot.datos.salaDescripcion}</div></div>
          {ot.datos.planCodigo !== null && <div className="dato"><div className="etiqueta-dato">Plan</div><div className="valor-dato">{ot.datos.planCodigo}</div></div>}
        </div>
      )}

      {ot.datos !== null && ot.datos.lineas.length > 0 && (
        <div className="sep">
          <h3>Tareas</h3>
          <ul aria-label="Tareas" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {ot.datos.lineas.map((l) => (
              <li key={l.id} className="fila" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <EstadoLinea estado={l.estado} />
                <span>{l.descripcionTarea ?? l.tipoDescripcion}</span>
                <span className="cod pequena">{l.ubicacionCodigo}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {ot.datos !== null && (
        <div className="sep">
          <h3>Acciones</h3>
          {!acciones.puedeReprogramar && !acciones.puedeRedimensionar && !acciones.puedeAsignar && acciones.motivosBloqueo.length > 0 && (
            <p className="nota aviso" role="status">Esta OT no admite cambios desde el Planner: {acciones.motivosBloqueo.join(' ')}</p>
          )}
          {/*  F10.4: si se puede asignar pero no reprogramar, decir también por qué no se puede mover. */}
          {!acciones.puedeReprogramar && acciones.puedeAsignar && acciones.motivosBloqueo.length > 0 && (
            <p className="nota" role="status">No se puede reprogramar: {acciones.motivosBloqueo.join(' ')}</p>
          )}
          <div className="fila" style={{ gap: 8, flexWrap: 'wrap' }}>
            {acciones.puedeReprogramar && (
              <button type="button" className="secundario" aria-expanded={accion === 'reprogramar'} onClick={() => alternar('reprogramar')}>
                Reprogramar
              </button>
            )}
            {acciones.puedeRedimensionar && (
              <button type="button" className="secundario" aria-expanded={accion === 'duracion'} onClick={() => alternar('duracion')}>
                Ajustar duración
              </button>
            )}
            {(acciones.puedeAsignar || acciones.puedeDesasignar) && (
              <button type="button" className="secundario" aria-expanded={accion === 'asignar'} onClick={() => alternar('asignar')}>
                Asignaciones
              </button>
            )}
          </div>
          {accion === 'reprogramar' && (
            <FormularioReprogramar idCentro={idCentro} evento={evento} zonaHoraria={zonaHoraria} alGuardado={guardadoDesdeAccion} alCancelar={() => setAccion('ninguna')} verAvisosPlanner={verAvisosPlanner} />
          )}
          {accion === 'duracion' && (
            <FormularioAjustarDuracion idCentro={idCentro} evento={evento} zonaHoraria={zonaHoraria} alGuardado={guardadoDesdeAccion} alCancelar={() => setAccion('ninguna')} verAvisosPlanner={verAvisosPlanner} />
          )}
          {accion === 'asignar' && (
            <FormularioAsignaciones idCentro={idCentro} evento={evento} acciones={acciones} alGuardado={guardadoDesdeAccion} alCancelar={() => setAccion('ninguna')} verAvisosPlanner={verAvisosPlanner} />
          )}
        </div>
      )}
    </Modal>
  );
}

function PanelDetalleProyeccion({ evento, cerrar }: { evento: PlannerEventoProyectado; cerrar: () => void }): ReactNode {
  return (
    <Modal titulo="Trabajo proyectado" cerrar={cerrar}>
      <p className="nota info" role="status">Esta ocurrencia todavía no se ha materializado como OT.</p>
      <div className="datos">
        <div className="dato"><div className="etiqueta-dato">Plan</div><div className="valor-dato">{evento.codigoPlan}</div></div>
        <div className="dato"><div className="etiqueta-dato">Fecha</div><div className="valor-dato">{evento.fecha} {evento.hora.slice(0, 5)}</div></div>
        <div className="dato"><div className="etiqueta-dato">Sala</div><div className="valor-dato">{evento.codigoSala}</div></div>
        <div className="dato"><div className="etiqueta-dato">Duración estimada</div><div className="valor-dato">{horas(evento.duracionEstimadaMin)}</div></div>
      </div>
      <div className="sep">
        <h3>Tareas</h3>
        <ul>
          {evento.lineas.map((l) => <li key={l.idPlanLinea}>{l.referenciaTarea}</li>)}
        </ul>
      </div>
    </Modal>
  );
}
